import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { TFunction } from 'i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';
import { selectStudioPeriod, setStudioPeriod } from '@/store/slices/studioPeriod';
import { NEWTON_LOGO_LIGHT_SRC } from '@/utils/nShowcase';

import { Button, Icon, Skeleton, Toast } from '../../components';
import { useToast } from '../../hooks/useToast';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { MOBILE } from '../../theme/tokens';
import type { StatsDelta } from '../../mock-data/types';
import { deltaPct } from '../home/stats-shape';
import { homePeriodOfWebsite, websitePeriodFor } from '../home/shared-period';
import { isOwnerTracked } from '../creatives/affiliate-campaign';
import { IntervalDropdown, type CustomRangeChoice, type IntervalOption, type OptionSearch } from '../home/components';
import { SectionHead, StatCard } from '../stats/components';
import { useBrandSettled } from '../website/useBrandSettled';
import { useLandingDestinations } from '../creatives/hooks/useLandingDestinations';
import { useBrandLinks } from '../creatives/useBrandLinks';
import { primaryBrandLogoUrl } from '../creatives/brand-logo';
import { assetKindOpens, useAssetOverlay } from '../creatives/components/AssetComposerOverlay';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { dateLocale } from '../../utils/locale';
import { countryName } from '../casino-settings/country-codes';
import {
  CountryFlag,
  DataNotes,
  FiltersPill,
  OptionGlyph,
  ShortcutPill,
  FiltersSheet,
  KpiTile,
  Notice,
  PageConversions,
  PageList,
  RankedList,
  ShareGlyph,
  ShareTiles,
  ExportPdfButton,
  ExportSnackbar,
  Summary,
  SummaryFilters,
  TrafficBreakdown,
  TrafficTrend,
  type FilterSection,
  type KpiFigure,
  type LandingOption,
  type PickedPage,
  type RankedItem,
  type ShareGlyphName,
  type ShareTile,
  type SummaryFilter,
} from './components';
import {
  comparisonCaption,
  deviceName,
  formatCount,
  formatDuration,
  hostKindName,
  intervalLabel,
  pageName,
  periodPhrase,
  previousPhrase,
  periodDelta,
  sourceName,
  systemName,
  viewsText,
} from './format';
import {
  DEFAULT_INTERVAL,
  FILTER_PARAMS,
  NO_FILTERS_OF,
  pageKey,
  pageOfKey,
  readFilters,
  writeFilters,
  type WebsiteFilters,
  type ChannelPick,
  type HostSelection,
  type LinkPick,
} from './filters';
import {
  ALL_SEL,
  allTick,
  appliedSel,
  channelTick,
  isAll,
  platformTick,
  resolveMatch,
  selectionEmpty,
  tickOf,
  toggleAllSel,
  affiliateOf,
  affiliateTick,
  emptyLinks,
  linkTick,
  toggleAffiliate,
  toggleLink,
  toggleChannel,
  togglePlatform,
  toggleValue,
  type Sel,
  type TickState,
} from './selection';
import {
  CHANNELS,
  MAX_RANGE_DAYS,
  PLATFORMS,
  WEBSITE_INTERVALS,
  channelTotals,
  attributionActive,
  countryCode,
  countsArrivals,
  creditedOnly,
  selectionIssue,
  customRange,
  fitsFilters,
  isChannel,
  isPreset,
  pathOfUrl,
  platformName,
  topPagesQuery,
  type Channel,
  type DeviceKind,
  type DeviceRow,
  type PageRow,
  type PageScope,
  type SourceReach,
  type SourceRow,
  type SystemKind,
  type SystemRow,
  type TrendPoint,
  type WebsitePeriod,
} from './queries';
import type { HostKind } from './api';
import { addressSection, toggleAddress, type AddressSection } from './quality/addresses';
import { compileFilters, rangeOf } from './quality/compile';
import type { QualityScreenState } from './quality/errors';
import { useWebsiteTrafficQualityGate } from './quality/gate';
import { hostKindLabel } from './quality/hosts';
import { dataNotes } from './quality/notices';
import { qualityReportPdf } from './quality/reportModel';
import { sourceCard, type SourceTile } from './quality/shape';
import type {
  QualityExposure,
  QualityLinkFacetRow,
  QualityReport,
  QualitySourceRow,
  QualityValueRow,
} from './quality/types';
import { peopleWord, countLine, footnote as qualityFootnote } from './quality/wording';
import { scopeOf, useWebsiteAnalytics, type WebsiteAnalytics, type WebsiteQuery } from './useWebsiteAnalytics';
import { badgeCount, useQualityFacets, type FacetName, type FacetSlot } from './useQualityFacets';
import { useQualityReport, type QualityReportState } from './useQualityReport';
import { useAffiliateDirectory } from './useAffiliateDirectory';
import {
  canReuseFilterCounts,
  canReuseLinks,
  countSignatures,
  linkPickerAffiliates,
  linkRowStatuses,
  linksCredited,
  unavailableSections,
  useFilterCounts,
  type ChoiceCount,
} from './useFilterCounts';
import {
  Page,
  Tiles,
  Split,
  Column,
  Footnote,
  Controls,
  CardDivider,
  CardSection,
  CardSectionTitle,
  CardSectionSub,
  NoteAction,
  SummaryActions,
  UpdatedLine,
} from './website-analytics.styles';
import { Frame as NoticeFrame, Title as NoticeTitle, Body as NoticeBody } from './components/Notice/Notice.styles';
import { REPORT_SERIES_RGB, downloadWebsiteReport, type ReportTable, type WebsiteReport } from './report-pdf';

/** The change against the period before (as the Stats page shows it), or null when either side was not measured. */
const changeOf = (current: number | null | undefined, previous: number | null | undefined): StatsDelta | null =>
  current === null || current === undefined || previous === null || previous === undefined
    ? null
    : periodDelta(current, previous);

/** The time tile's unit: sec under one minute, min from one minute. Pure. */
const timeUnit = (seconds: number): 'sec' | 'min' => (seconds < 60 ? 'sec' : 'min');

const timeFigure = (seconds: number | null, t: TFunction): KpiFigure | null => {
  if (seconds === null) return null;
  return timeUnit(seconds) === 'sec'
    ? { value: Math.round(seconds), suffix: ` ${t('sec')}` }
    : { value: Math.round((seconds / 60) * 10) / 10, decimals: 1, suffix: ` ${t('min')}` };
};

/** What the numbers are narrowed to, page-wise: one landing page, one page of the website, several pages, or none. */
type PageMode = 'landing' | 'site' | 'pages' | null;

/** How the people and visits are counted: as the arrivals through the picked links, affiliate (the owner's own links
 *  read as 'own'), campaign or sources (one or several), else credited (null). */
type Arrivals = 'link' | 'links' | 'affiliate' | 'own' | 'campaign' | 'source' | 'sources' | null;

/** The summary's sentences, worded for an owner (no metric names, no jargon). `w` words a string that counts visitors
 *  (as people in quality mode). */
const summaryLines = (
  a: Pick<WebsiteAnalytics, 'totals' | 'previousTotals' | 'time'>,
  interval: WebsitePeriod,
  t: TFunction,
  language: string,
  onPage: PageMode,
  narrowed = false,
  arrivals: Arrivals = null,
  w: (key: string) => string = (key) => key,
): { lead: string; follow: string[] } => {
  const period = periodPhrase(interval, t, language);
  const previous = previousPhrase(interval, t);
  if (a.totals.status === 'error' || !a.totals.data) {
    return { lead: t('We could not load your visitor numbers.'), follow: [t('Please try again in a moment.')] };
  }
  const { visitors, visits, views } = a.totals.data;
  // Narrowed to a source or a link, a page view counts under what brought it, a person under what last brought them:
  // the pages someone viewed through this one still count here after a later source brings them back.
  // A link's or a source's people are those who arrived through it in the period; its page views can still come from
  // people who arrived through it earlier (it keeps their credit for 30 days).
  if (visitors === 0 && arrivals === 'link' && views > 0) {
    return {
      lead: t('No one arrived through this link {{period}}.', { period }),
      follow: [
        views === 1
          ? t('1 page viewed comes from someone who arrived through it earlier.')
          : t('{{n}} pages viewed come from people who arrived through it earlier.', {
              n: formatCount(views, language),
            }),
      ],
    };
  }
  if (
    visitors === 0 &&
    (arrivals === 'affiliate' ||
      arrivals === 'own' ||
      arrivals === 'campaign' ||
      arrivals === 'links' ||
      arrivals === 'sources') &&
    views > 0
  ) {
    return {
      lead:
        arrivals === 'affiliate'
          ? t("No one arrived through this affiliate's links {{period}}.", { period })
          : arrivals === 'own'
            ? t('No one arrived through your own links {{period}}.', { period })
            : arrivals === 'campaign'
              ? t("No one arrived through this campaign's links {{period}}.", { period })
              : arrivals === 'links'
                ? t('No one arrived through these links {{period}}.', { period })
                : t('No one arrived from these sources {{period}}.', { period }),
      follow: [
        views === 1
          ? t('1 page viewed comes from someone who arrived through them earlier.')
          : t('{{n}} pages viewed come from people who arrived through them earlier.', {
              n: formatCount(views, language),
            }),
      ],
    };
  }
  if (visitors === 0 && arrivals === 'source' && views > 0) {
    return {
      lead: t('No one arrived from this source {{period}}.', { period }),
      follow: [
        views === 1
          ? t('1 page viewed comes from someone who arrived from it earlier.')
          : t('{{n}} pages viewed come from people who arrived from it earlier.', {
              n: formatCount(views, language),
            }),
      ],
    };
  }
  if (visitors === 0 && narrowed && views > 0) {
    return {
      lead: t(w('No visitors counted here {{period}}.'), { period }),
      follow: [
        views === 1
          ? t('1 page was viewed through it, by someone a later source or link brought back, so they count there.')
          : t(
              '{{n}} pages were viewed through it, by people a later source or link brought back, so they count there.',
              {
                n: formatCount(views, language),
              },
            ),
      ],
    };
  }
  if (visitors === 0) {
    if (onPage === 'pages') {
      return {
        lead: t('No visits to these pages yet {{period}}.', { period }),
        follow: [t('Visits show up here a few minutes after people open the pages.')],
      };
    }
    if (onPage) {
      return {
        lead:
          onPage === 'landing'
            ? t('No visits to this landing page yet {{period}}.', { period })
            : t('No visits to this page yet {{period}}.', { period }),
        follow: [t('Visits show up here a few minutes after people open the page.')],
      };
    }
    return {
      lead: t('No visits yet {{period}}.', { period }),
      follow: [t('Visits show up here a few minutes after people open your website.')],
    };
  }
  const count = formatCount(visitors, language);
  let lead: string;
  if (onPage === 'landing') {
    lead =
      visitors === 1
        ? t(w('1 person visited this landing page {{period}}.'), { period })
        : t(w('{{visitors}} people visited this landing page {{period}}.'), { visitors: count, period });
  } else if (onPage === 'site') {
    lead =
      visitors === 1
        ? t(w('1 person visited this page {{period}}.'), { period })
        : t(w('{{visitors}} people visited this page {{period}}.'), { visitors: count, period });
  } else if (onPage === 'pages') {
    lead =
      visitors === 1
        ? t(w('1 person visited these pages {{period}}.'), { period })
        : t(w('{{visitors}} people visited these pages {{period}}.'), { visitors: count, period });
  } else if (arrivals === 'links') {
    lead =
      visitors === 1
        ? t(w('1 person came through these links {{period}}.'), { period })
        : t(w('{{visitors}} people came through these links {{period}}.'), { visitors: count, period });
  } else if (arrivals === 'link') {
    lead =
      visitors === 1
        ? t(w('1 person came through this link {{period}}.'), { period })
        : t(w('{{visitors}} people came through this link {{period}}.'), { visitors: count, period });
  } else if (arrivals === 'affiliate') {
    lead =
      visitors === 1
        ? t(w("1 person came through this affiliate's links {{period}}."), { period })
        : t(w("{{visitors}} people came through this affiliate's links {{period}}."), { visitors: count, period });
  } else if (arrivals === 'own') {
    lead =
      visitors === 1
        ? t(w('1 person came through your own links {{period}}.'), { period })
        : t(w('{{visitors}} people came through your own links {{period}}.'), { visitors: count, period });
  } else if (arrivals === 'campaign') {
    lead =
      visitors === 1
        ? t(w("1 person came through this campaign's links {{period}}."), { period })
        : t(w("{{visitors}} people came through this campaign's links {{period}}."), { visitors: count, period });
  } else {
    lead =
      visitors === 1
        ? t(w('1 person visited your website {{period}}.'), { period })
        : t(w('{{visitors}} people visited your website {{period}}.'), { visitors: count, period });
  }
  const follow: string[] = [];
  const prev = a.previousTotals.data?.visitors;
  if (a.previousTotals.status === 'ready' && prev !== undefined) {
    if (prev === 0) {
      follow.push(t(w('There were no visitors {{previous}}.'), { previous }));
    } else {
      const pct = Math.round(deltaPct(visitors, prev));
      if (Math.abs(pct) < 1) follow.push(t('That is about the same as {{previous}}.', { previous }));
      else if (pct > 0) follow.push(t('That is {{pct}}% more than {{previous}}.', { pct, previous }));
      else follow.push(t('That is {{pct}}% fewer than {{previous}}.', { pct: -pct, previous }));
    }
  }
  if (onPage) {
    // On the picked pages a visit's length is the time spent on them, and pages per visit says nothing.
    const seconds = a.time.status === 'ready' ? a.time.data : null;
    if (seconds !== null && seconds !== undefined) {
      follow.push(
        onPage === 'pages'
          ? t('People spend {{time}} on these pages on average.', { time: formatDuration(seconds, t) })
          : t('People spend {{time}} on this page on average.', { time: formatDuration(seconds, t) }),
      );
    }
  } else if (visits > 0 && !arrivals) {
    // Through a link or a source the visits count the arrivals and the views those credited to it: pages per visit
    // would mix them.
    const perVisit = Math.round((views / visits) * 10) / 10;
    const pages = new Intl.NumberFormat(language || undefined, { maximumFractionDigits: 1 }).format(perVisit);
    const seconds = a.time.status === 'ready' ? a.time.data : null;
    if (seconds !== null && seconds !== undefined) {
      follow.push(
        perVisit <= 1.05
          ? t('An average visit lasts {{time}}, usually on a single page.', { time: formatDuration(seconds, t) })
          : t('An average visit lasts {{time}} and covers {{pages}} pages.', {
              time: formatDuration(seconds, t),
              pages,
            }),
      );
    } else if (perVisit > 1.05) {
      follow.push(t('An average visit covers {{pages}} pages.', { pages }));
    }
  }
  return { lead, follow };
};

const DEVICE_GLYPHS: Record<DeviceKind, ShareGlyphName> = {
  mobile: 'phone',
  desktop: 'computer',
  tablet: 'tablet',
  other: 'device',
};

const SYSTEM_GLYPHS: Record<SystemKind, ShareGlyphName> = {
  ios: 'apple',
  macos: 'apple',
  android: 'android',
  windows: 'windows',
  other: 'system',
};

/** A share of the whole as a whole percent. */
const percentOf = (n: number, total: number): string => `${Math.round((n / Math.max(1, total)) * 100)}%`;

/** The pickers' "everything" choice, and the link picker's "no link" one (`?link=none`). */
const ALL = 'all';
const NO_LINK = 'none';

/** A platform's choice in the source picker: its source and its key (a platform belongs to its source). */
const PLATFORM_SEP = '::';
const platformValue = (channel: Channel, key: string): string => `${channel}${PLATFORM_SEP}${key}`;

/** Each source's picture. */
const SOURCE_GLYPHS: Record<Channel, ShareGlyphName> = {
  'Organic Search': 'search',
  'Paid Search': 'cursor',
  'Organic Social': 'social',
  'Paid Social': 'megaphone',
  AI: 'chat',
  Referral: 'link',
  Email: 'mail',
  'Other Paid': 'target',
  Direct: 'bookmark',
  Unknown: 'question',
};

/** The platforms' own websites, for the overview's link out. */
const PLATFORM_SITES: Record<string, string> = {
  google: 'https://www.google.com',
  bing: 'https://www.bing.com',
  duckduckgo: 'https://duckduckgo.com',
  yahoo: 'https://www.yahoo.com',
  ecosia: 'https://www.ecosia.org',
  yandex: 'https://yandex.com',
  meta: 'https://www.facebook.com',
  instagram: 'https://www.instagram.com',
  youtube: 'https://www.youtube.com',
  x: 'https://x.com',
  linkedin: 'https://www.linkedin.com',
  reddit: 'https://www.reddit.com',
  tiktok: 'https://www.tiktok.com',
  chatgpt: 'https://chatgpt.com',
  perplexity: 'https://www.perplexity.ai',
  gemini: 'https://gemini.google.com',
  copilot: 'https://copilot.microsoft.com',
  claude: 'https://claude.ai',
};

/** The platforms' brand marks (Gemini shows Google's); any other platform takes its source's picture. */
const PLATFORM_GLYPHS: Record<string, ShareGlyphName> = {
  google: 'google',
  bing: 'bing',
  duckduckgo: 'duckduckgo',
  yahoo: 'yahoo',
  ecosia: 'ecosia',
  yandex: 'yandex',
  meta: 'facebook',
  instagram: 'instagram',
  youtube: 'youtube',
  x: 'x',
  linkedin: 'linkedin',
  reddit: 'reddit',
  tiktok: 'tiktok',
  chatgpt: 'openai',
  perplexity: 'perplexity',
  gemini: 'google',
  copilot: 'copilot',
  claude: 'claude',
};

/** A platform's picture and website: only its own entries (a platform comes from the address, `?platform=constructor`
 *  must not reach the object's prototype). */
const platformGlyph = (key: string): ShareGlyphName | undefined =>
  Object.hasOwn(PLATFORM_GLYPHS, key) ? PLATFORM_GLYPHS[key] : undefined;
const platformSite = (key: string): string | undefined =>
  Object.hasOwn(PLATFORM_SITES, key) ? PLATFORM_SITES[key] : undefined;

/** A web address typed in a search as the analytics service names a website (its host, without `www.`); null when it
 *  is not one. Pure. */
const webHost = (typed: string): string | null => {
  const host = typed
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : null;
};

/** The brand's logo for a light page: its black wordmark (the logo set's `name_horizontal_black`, made for light
 *  backgrounds), else its primary logo. Pure. */
const brandLogoForLight = (raw: unknown): string | undefined => {
  if (raw && typeof raw === 'object') {
    const black = (raw as Record<string, { src?: unknown } | undefined>).name_horizontal_black?.src;
    if (typeof black === 'string' && black) return black;
  }
  return primaryBrandLogoUrl(raw);
};

/** The Whitelabels lockup for a light page (the sign-in page's light-theme logo). */
const WHITELABELS_LOGO_LIGHT_SRC = '/assets/config-v3/logo-light.svg';

/** Whether any card is still loading (a failed one has settled: the document shows its figures as "-"). Pure. */
const cardsLoading = (a: WebsiteAnalytics): boolean =>
  [
    a.totals,
    a.previousTotals,
    a.time,
    a.previousTime,
    a.trend,
    a.pages,
    a.devices,
    a.systems,
    a.sites,
    a.previousDevices,
    a.previousSystems,
    a.previousSites,
    ...(a.attributionAvailable ? [a.sources, a.previousSources, a.links, a.sourceReach] : []),
  ].some((section) => section.status === 'loading');

/** A figure's change as the report shows it (null: not compared). Pure. */
const kpiChange = (change: StatsDelta | null): { change: string | null; dir: 'up' | 'down' | 'flat' } =>
  change ? { change: change.label, dir: change.dir } : { change: null, dir: 'flat' };

/** Several names as a filter's current choice ("Romania, Mexico"). */
const listOf = (names: readonly string[]): string => names.join(', ');

const NO_FILTERS: WebsiteFilters = { interval: DEFAULT_INTERVAL, ...NO_FILTERS_OF };

/** No affiliates or campaigns to count (the sheet's section is shut). */
const NONE_WANTED: readonly string[] = [];
/** No link facet rows read (yet). */
const NO_LINK_ROWS: readonly QualityLinkFacetRow[] = [];

/** A flat filter's tick for one of its choices (its "All" choice included). Pure. */
const flatTick = (sel: Sel, value: string, all = ALL): TickState => (value === all ? allTick(sel) : tickOf(sel, value));

/** A flat filter after a click on one of its choices (its "All" choice included). Pure. */
const flatToggle = (sel: Sel, value: string, all = ALL): Sel =>
  value === all ? toggleAllSel(sel) : toggleValue(sel, value);

const queryOf = (f: Omit<WebsiteFilters, 'interval' | 'hosts'>): WebsiteQuery => {
  const pageSel = appliedSel(f.pages);
  const pages = pageSel.values.map(pageOfKey).filter((pg): pg is PageScope => pg !== null);
  return {
    pages,
    pagesOut: pageSel.mode === 'out' && pages.length > 0,
    attribution: { channels: f.channels, links: f.links, campaigns: resolveMatch(f.campaigns) },
    countries: resolveMatch(f.countries),
  };
};

/** The sources whose visits name no platform worth splitting (typed in, or not known). */
const drillsDown = (channel: Channel | null): channel is Channel =>
  channel !== null && channel !== 'Direct' && channel !== 'Unknown';

/** How many platforms the sources card shows one by one; the rest are summed into one tile. */
const TOP_PLATFORMS = 5;
const OTHER_TILE = '__other';

/** Nothing is read yet: no brand, or its addresses are still being read. */
const waitingForData = (tenantId: number | undefined, phase: WebsiteAnalytics['phase']): boolean =>
  tenantId === undefined || phase === 'loading';

const visitorsText = (n: number, t: TFunction, language: string, quality = false): string =>
  n === 1
    ? t(peopleWord('1 visitor', quality))
    : t(peopleWord('{{n}} visitors', quality), { n: formatCount(n, language) });

/** A device as the analytics service names it, by kind. Pure. */
const deviceKindOf = (raw: string): DeviceKind => {
  const v = raw.toLowerCase();
  return v === 'mobile' || v === 'desktop' || v === 'tablet' ? v : 'other';
};

const SYSTEM_KINDS: Record<string, SystemKind> = { ios: 'ios', android: 'android', windows: 'windows', macos: 'macos' };
/** A system as the analytics service names it, by kind (read only through its own entries). Pure. */
const systemKindOf = (raw: string): SystemKind => {
  const v = raw.toLowerCase();
  return Object.hasOwn(SYSTEM_KINDS, v) ? SYSTEM_KINDS[v] : 'other';
};

/** A quality report's device or system rows summed by kind, most first (systems with Other last), as the aggregate
 *  cards shape theirs; null when a row has no count (the card shows the unavailable line, never 0). Pure. */
const kindRows = <K extends string>(
  rows: readonly QualityValueRow[],
  kindOf: (raw: string) => K,
  otherLast = false,
): { kind: K; visitors: number }[] | null => {
  const acc = new Map<K, number>();
  for (const r of rows) {
    if (r.people === null) return null;
    const kind = kindOf(r.value);
    acc.set(kind, (acc.get(kind) ?? 0) + r.people);
  }
  return [...acc.entries()]
    .map(([kind, visitors]) => ({ kind, visitors }))
    .filter((d) => d.visitors > 0)
    .sort(
      (x, y) => (otherLast ? Number(x.kind === 'other') - Number(y.kind === 'other') : 0) || y.visitors - x.visitors,
    );
};

/** A trend bucket's start as a time (the service may write microseconds; Date.parse reads milliseconds). Pure. */
const bucketAt = (start: string): number => Date.parse(start.replace(/(\.\d{3})\d+/, '$1'));

/** The headline figures; a quality report's people and visits are `null` where the service could not count them. */
type ShownTotals = { views: number; visitors: number | null; visits: number | null };

/** The report's figures in the shapes the screen draws, with each count the service could not give marked. Pure. */
type QualityShown = {
  totals: ShownTotals;
  previousTotals: ShownTotals;
  time: number | null;
  previousTime: number | null;
  /** Every trend point has a People count (else the trend draws pages viewed only). */
  trendVisitors: boolean;
  trend: TrendPoint[];
  suspectedTrend: TrendPoint[];
  excludedTrend: TrendPoint[];
  pages: PageRow[];
  devices: DeviceRow[] | null;
  previousDevices: DeviceRow[] | null;
  systems: SystemRow[] | null;
  previousSystems: SystemRow[] | null;
  /** The selected addresses' people, with the period before; null when a count is missing. */
  hosts: { host: string; kind: 'site' | 'landing'; visitors: number; previous: number | null }[] | null;
};

const qualityShown = (report: QualityReport): QualityShown => {
  const { current, previous } = report;
  const now = current.cards.primary;
  const before = previous.cards.primary;
  const previousViews = new Map(previous.topPages.map((p) => [p.path, p.pageViews]));
  const kindOfHost = new Map(
    report.hosts.map((h) => [h.host, hostKindLabel(h) === 'Landing pages' ? ('landing' as const) : ('site' as const)]),
  );
  const hostsBefore = new Map(previous.hosts.map((h) => [h.host, h.people]));
  const hosts = current.hosts.every((h) => h.people !== null)
    ? current.hosts.map((h) => ({
        host: h.host,
        kind: kindOfHost.get(h.host) ?? ('site' as const),
        visitors: h.people ?? 0,
        // No row before: no people then; a row the service could not count: not compared.
        previous: hostsBefore.has(h.host) ? hostsBefore.get(h.host) ?? null : 0,
      }))
    : null;
  return {
    totals: { views: now.pageViews, visitors: now.people, visits: now.visits },
    previousTotals: { views: before.pageViews, visitors: before.people, visits: before.visits },
    time: now.averageSeconds,
    previousTime: before.averageSeconds,
    trendVisitors: current.trend.every((p) => p.unflagged.people !== null),
    trend: current.trend.map((p) => ({
      at: bucketAt(p.start),
      visitors: p.unflagged.people ?? 0,
      views: p.unflagged.pageViews,
    })),
    suspectedTrend: current.trend.map((p) => ({
      at: bucketAt(p.start),
      visitors: p.suspected.people ?? 0,
      views: p.suspected.pageViews,
    })),
    excludedTrend: current.trend.map((p) => ({
      at: bucketAt(p.start),
      visitors: p.excluded.people ?? 0,
      views: p.excluded.pageViews,
    })),
    pages: current.topPages.map((p) => ({
      path: p.path,
      views: p.pageViews,
      visitors: p.people ?? 0,
      avgSeconds: p.pageAverageSeconds,
      previousViews: previousViews.get(p.path) ?? 0,
    })),
    devices: kindRows(current.devices, deviceKindOf),
    previousDevices: kindRows(previous.devices, deviceKindOf),
    systems: kindRows(current.systems, systemKindOf, true),
    previousSystems: kindRows(previous.systems, systemKindOf, true),
    hosts,
  };
};

/** The report's totals as the summary's sentences read them; people before that were not counted are not compared.
 *  Pure. */
const qualitySummaryInput = (
  totals: ShownTotals | null,
  previous: ShownTotals | null,
  time: number | null,
): Pick<WebsiteAnalytics, 'totals' | 'previousTotals' | 'time'> => ({
  totals: totals
    ? { status: 'ready', data: { views: totals.views, visitors: totals.visitors ?? 0, visits: totals.visits ?? 0 } }
    : { status: 'loading', data: null },
  previousTotals:
    previous && previous.visitors !== null
      ? { status: 'ready', data: { views: previous.views, visitors: previous.visitors, visits: previous.visits ?? 0 } }
      : { status: 'loading', data: null },
  time: { status: 'ready', data: time },
});

/**
 * A source facet's counts as the source picker reads them: per channel and per platform, its arrivals and its credited
 * people (`null` where the service could not give a count, shown as `-`). A channel's credited people add its
 * platforms' rows, unknown when one of them is. The rows rank the platforms found only by the search. Pure.
 */
const qualitySourceCounts = (
  rows: readonly QualitySourceRow[],
  channelArrivals: readonly { channel: Channel; arrivalPeople: number | null }[],
): {
  credited: Map<string, number | null>;
  reach: Map<string, number | null>;
  creditedChannels: Map<Channel, number | null>;
  reachChannels: Map<Channel, number | null>;
  creditedRows: SourceRow[];
  reachRows: SourceRow[];
} => {
  const credited = new Map<string, number | null>();
  const reach = new Map<string, number | null>();
  const creditedChannels = new Map<Channel, number | null>();
  rows.forEach((r) => {
    credited.set(platformValue(r.channel, r.platform), r.creditedPeople);
    reach.set(platformValue(r.channel, r.platform), r.arrivalPeople);
    const sum = creditedChannels.has(r.channel) ? creditedChannels.get(r.channel) ?? null : 0;
    creditedChannels.set(r.channel, sum === null || r.creditedPeople === null ? null : sum + r.creditedPeople);
  });
  return {
    credited,
    reach,
    creditedChannels,
    reachChannels: new Map(channelArrivals.map((c) => [c.channel, c.arrivalPeople])),
    creditedRows: rows.map((r) => ({ channel: r.channel, platform: r.platform, visitors: r.creditedPeople ?? 0 })),
    reachRows: rows.map((r) => ({ channel: r.channel, platform: r.platform, visitors: r.arrivalPeople ?? 0 })),
  };
};

/**
 * A picker choice's count from a facet's rows (`null`, shown as `-`, where it is unknown). A choice with no row has
 * none (0), unless the list was cut (`truncated`): its row may then only be past the cap. A count `summed` from the
 * listed rows (a channel's credited people) is unknown whenever the list was cut. Pure.
 */
const facetCount = <K,>(
  counts: ReadonlyMap<K, number | null>,
  key: K,
  truncated: boolean,
  summed = false,
): number | null => {
  if (truncated && summed) return null;
  if (counts.has(key)) return counts.get(key) ?? null;
  return truncated ? null : 0;
};

/** How old a report's data was when it was made (as its data note measures it); null when that could not be checked.
 *  Pure. */
const dataAgeMs = (report: QualityReport): number | null => {
  const { status, lastSuccessfulRefreshStartedAt: started } = report.meta.refreshObservation;
  if (status === 'unavailable' || started === null) return null;
  const age = Date.parse(report.meta.generatedAt) - Date.parse(started);
  return Number.isNaN(age) ? null : age;
};

const MINUTE_MS = 60_000;
/** Data older than this is a data note; up to it, a quiet line under the tiles says how old it is. */
const FRESH_MS = 15 * MINUTE_MS;

/** The addresses the pickers count, once the report has settled (landed or failed); null before. Pure. */
const settledHostsOf = (qr: QualityReportState): readonly string[] | null => {
  if (qr.status === 'no-selection' || qr.status === 'no-site') return [];
  if (qr.status === 'ready' || qr.status === 'error') return qr.report?.scope.selectedHosts ?? null;
  return null;
};

const GROUP_PREFIX = 'group:';
const HOST_PREFIX = 'host:';
/** The Filters sheet's Addresses section (its key, which "Choose addresses" opens the sheet on). */
const ADDRESSES = 'host';
const isExposure = (v: string): v is QualityExposure => v === 'public' || v === 'internal' || v === 'unclassified';

/**
 * Stats → Website: how many people visit the brand's website and landing pages, how long they stay, which
 * pages they open and on what device, for a chosen period against the one before it. Written for owners,
 * not analysts: every figure carries a one-line meaning and the top of the tab answers in a sentence.
 *
 * Data: spark-creatives' owner-scoped proxy to the analytics service (see {@link useWebsiteAnalytics}).
 * The brand is the lagged `displayBrand`, like the Stats tab, so a brand switch fades over cleanly.
 */
const WebsiteAnalyticsContent = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const brandName = displayBrand?.casino_data?.details?.brand ?? '';
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  // Quality mode (website stats on and the brand on the rollout list): the figures come from one quality report and
  // never from aggregate totals. Off, the tab reads as it always has.
  const gate = useWebsiteTrafficQualityGate(tenantId);
  const quality = gate.quality;
  const w = React.useCallback((key: string): string => peopleWord(key, quality), [quality]);

  // The filters live in the address (see filters.ts); changing one replaces the entry rather than adding history.
  const [params, setParams] = useSearchParams();
  // The period: the address's when it names one (a shared or deep link), else the one the owner last picked on any
  // page with a range, as closely as this tab can show it (see `websitePeriodFor`), else the default.
  const studioPeriod = useAppSelector(selectStudioPeriod);
  const addressFilters = readFilters(params);
  const filters =
    params.get(FILTER_PARAMS.period) === null && studioPeriod !== null
      ? { ...addressFilters, interval: websitePeriodFor(studioPeriod) }
      : addressFilters;
  const interval = filters.interval;
  // The filters by value: the address is read afresh on each render, so choices are compared by their contents.
  const filtersKey = JSON.stringify(filters);
  const {
    pages: pageSel,
    channels: sourceSel,
    links: linkSel,
    campaigns: campaignSel,
    countries: countrySel,
    hosts: hostSel,
  } = React.useMemo(() => JSON.parse(filtersKey) as WebsiteFilters, [filtersKey]);
  const updateFilters = React.useCallback(
    (next: Partial<WebsiteFilters>): void => setParams((cur) => writeFilters(cur, next), { replace: true }),
    [setParams],
  );
  // A period picked here goes in the address and, when Home can show it too (not a single past day), becomes the range
  // every other page opens on. Only a pick writes it: opening this tab on a period never does.
  const dispatch = useAppDispatch();
  const pickInterval = React.useCallback(
    (next: WebsitePeriod): void => {
      updateFilters({ interval: next });
      const shared = homePeriodOfWebsite(next);
      if (shared !== null) dispatch(setStudioPeriod(shared));
    },
    [updateFilters, dispatch],
  );
  // Syncs the address with the brand: pages, links and addresses belong to one brand, so switching brands drops them
  // (the new brand opens on its default addresses).
  const lastTenant = React.useRef(tenantId);
  React.useEffect(() => {
    if (lastTenant.current !== undefined && tenantId !== undefined && lastTenant.current !== tenantId) {
      updateFilters({ pages: ALL_SEL, links: null, campaigns: ALL_SEL, hosts: { kind: 'default' } });
    }
    if (tenantId !== undefined) lastTenant.current = tenantId;
  }, [tenantId, updateFilters]);

  // The brand's landing pages, one per path (a page is served on each of the brand's landing addresses).
  const landing = useLandingDestinations(tenantId);
  const landingPages = React.useMemo(() => {
    const seen = new Set<string>();
    const out: LandingOption[] = [];
    landing.destinations.forEach((d) => {
      const path = pathOfUrl(d.url);
      if (!path || seen.has(path)) return;
      seen.add(path);
      out.push({
        path,
        name: d.name || path,
        url: d.url,
        ...(d.imageUrl ? { imageUrl: d.imageUrl } : {}),
        ...(d.landingPageId ? { landingPageId: d.landingPageId } : {}),
      });
    });
    return out;
  }, [landing.destinations]);
  // A picked page, named from the brand's landing pages (or from its path, for a page of the website).
  const pickedOf = React.useCallback(
    (page: PageScope): PickedPage => {
      if (page.kind === 'site') return { kind: 'site', path: page.path, name: pageName(page.path, t) };
      const lp = landingPages.find((p) => p.path === page.path);
      return {
        kind: 'landing',
        path: page.path,
        name: lp?.name ?? page.path,
        ...(lp?.landingPageId ? { landingPageId: lp.landingPageId } : {}),
      };
    },
    [landingPages, t],
  );
  const pageScopes = React.useMemo(
    () => pageSel.values.map(pageOfKey).filter((pg): pg is PageScope => pg !== null),
    [pageSel],
  );
  const pagesOut = pageSel.mode === 'out' && pageScopes.length > 0;
  const pickedPages = React.useMemo(() => pageScopes.map(pickedOf), [pageScopes, pickedOf]);
  // One landing page, one page of the website, or several pages (their sentences say "these pages"); every page but
  // some reads as the website.
  const onPage: PageMode =
    pagesOut || pageScopes.length === 0 ? null : pageScopes.length === 1 ? pageScopes[0].kind : 'pages';
  const picked = !pagesOut && pickedPages.length === 1 ? pickedPages[0] : null;

  const query = React.useMemo(
    () =>
      queryOf({ pages: pageSel, channels: sourceSel, links: linkSel, campaigns: campaignSel, countries: countrySel }),
    [pageSel, sourceSel, linkSel, campaignSel, countrySel],
  );
  const attribution = query.attribution;
  // In quality mode this reads only the brand's addresses (and whether its environment attributes), no figures.
  const a = useWebsiteAnalytics(tenantId, interval, query, gate.mode);
  const setHosts = React.useCallback((next: HostSelection): void => updateFilters({ hosts: next }), [updateFilters]);
  // The source, link and campaign filters apply where the environment credits a visit to them, as the cards read them.
  // Filters that name one wait for this load's hosts read to say so (a cached yes is already known); others never wait.
  const attributionKnown = a.attributionAvailable || (a.current && a.phase !== 'loading');
  const qr = useQualityReport(
    tenantId,
    quality,
    filters,
    attributionKnown || !attributionActive(attribution) ? a.attributionAvailable : null,
    setHosts,
  );
  // The latest report of this brand and mode (its addresses feed the Filters sheet), and the one whose figures show: it
  // always answers the request on screen. An ordinary re-read shows placeholders, as the aggregate cards do; an earlier
  // answer to the same request stays in view only during the busy retry or after a failed read, with its data note.
  const latest = quality ? qr.latest : null;
  const report = quality ? qr.report : null;
  // The pickers count the report's own addresses once it has settled.
  const settledHosts = quality ? settledHostsOf(qr) : null;
  const qualityRange = rangeOf(interval);
  // Analytics compiles the nested picks itself in quality mode; the aggregate route takes one choice per filter where
  // its environment cannot.
  const conditions = quality || a.conditionsAvailable;

  // The source and link pickers keep one fixed list (every source; no link, then the brand's links), each choice with
  // its visitors in the period at its right: a placeholder while those load, 0 for none.
  const { links: brandLinks, reload: reloadLinks } = useBrandLinks();
  const { isEnabled } = useFeatureGate();
  const assetOverlay = useAssetOverlay({ onClosed: reloadLinks });
  const countMeta = React.useCallback(
    // A count the service could not give (null, quality mode) reads `-`, never 0.
    (status: ChoiceCount['status'], n: number | null | undefined): React.ReactNode => {
      switch (status) {
        case 'loading':
          return <Skeleton width={22} height={10} radius={4} />;
        case 'error':
        case 'unavailable':
          return '-';
        case 'ready':
          return n === null ? '-' : formatCount(n ?? 0, language);
        default: {
          // A status added later fails the typecheck here, never reads as a number.
          const unknown: never = status;
          return unknown;
        }
      }
    },
    [language],
  );
  // The filters open in one sheet, as a draft applied in one go (on a wider screen the Source pill opens it on Source).
  const phone = useMediaQuery(MOBILE);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetKey, setSheetKey] = React.useState<string | undefined>(undefined);
  const [filterMessage, setFilterMessage] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<WebsiteFilters>(filters);
  // The sheet's filter on screen: its choices' counts are read then (under the draft), not with every load.
  const [shownSection, setShownSection] = React.useState<string | null>(null);
  const openSheet = (key?: string): void => {
    setDraft(filters);
    setFilterMessage(null);
    setShownSection(null);
    setSheetKey(key);
    setSheetOpen(true);
  };
  // The time range stays in the bar beside the pill, so it is not one of the sheet's filters.
  const narrows = (sel: Sel): boolean => !isAll(appliedSel(sel));
  const activeFilters =
    Number(narrows(pageSel)) +
    Number(narrows(countrySel)) +
    Number(a.attributionAvailable && sourceSel !== null) +
    Number(a.attributionAvailable && (linkSel !== null || narrows(campaignSel))) +
    // A choice of addresses other than the brand's default is one more.
    Number(quality && hostSel.kind !== 'default');

  // Quality mode: each picker's counts from its facet, under the sheet's draft, once the report has settled. A facet the
  // sheet showed stays asked for until it closes, so moving on to another section never cancels its read.
  const draftFilters = React.useMemo(
    () => compileFilters(draft, a.attributionAvailable),
    [draft, a.attributionAvailable],
  );
  const wantedFacets: readonly FacetName[] =
    shownSection === 'source'
      ? ['source']
      : shownSection === 'link'
        ? ['link', 'link_groups']
        : shownSection === 'campaign'
          ? ['link_groups']
          : shownSection === 'country'
            ? ['country']
            : [];
  const facets = useQualityFacets({
    tenantId,
    enabled: quality,
    selectedHosts: settledHosts,
    range: qualityRange,
    filters: draftFilters,
    open: quality && sheetOpen,
    wanted: wantedFacets,
  });
  // A facet Analytics refused because the draft has too many conditions: the sheet says so (its counts read `-`).
  const facetsTooLarge =
    quality &&
    [facets.sources, facets.links, facets.countries, facets.linkGroups].some(
      (slot) => slot.code === 'filter_limit_exceeded',
    );
  const linkFacet = facets.links.data?.rows ?? NO_LINK_ROWS;

  // The affiliate and campaign filters' names: the brand's affiliates and campaigns in the affiliates platform, read
  // when the sheet opens or one of them is on. Studio's owner-tracked links all carry the owner's own affiliate token.
  const directory = useAffiliateDirectory(
    displayBrand?.id !== undefined && displayBrand?.id !== null ? String(displayBrand.id) : undefined,
    a.attributionAvailable && (sheetOpen || linkSel !== null || narrows(campaignSel)),
    sheetOpen,
  );
  const affiliateValues = React.useMemo(
    () =>
      linkPickerAffiliates(
        [
          ...(quality ? linkFacet.map((l) => l.affId) : (a.links.data ?? []).map((l) => l.affId)),
          ...brandLinks.map((l) => l.aff_id),
        ],
        [linkSel, draft.links],
      ),
    [quality, linkFacet, a.links.data, brandLinks, linkSel, draft.links],
  );
  const campaignValues = React.useMemo(() => directory.campaigns.map((e) => e.value), [directory.campaigns]);
  // The draft as the queries take it: the sheet's counts are read under it, so clearing or changing a filter there
  // counts the others' choices again.
  const draftQuery = React.useMemo(() => queryOf(draft), [draft]);
  const countsBase = {
    tenantId,
    hosts: a.hosts,
    span: a.window.current,
    attributionAvailable: a.attributionAvailable,
    conditionsAvailable: a.conditionsAvailable,
  };
  const draftSigs = countSignatures({ ...countsBase, ...draftQuery });
  const appliedSigs = countSignatures({ ...countsBase, ...query });
  // The source and link pickers reuse the cards' own figures while the filters they are counted under are the applied
  // ones (the cards find several affiliates' or campaigns' links under the picked sources, so not then for sources),
  // and only while the cards count them credited exactly when the picker does.
  const cardsRead = !quality && a.phase === 'ready' && a.current;
  const sourcesFromCards =
    cardsRead &&
    // Equal source keys already mean equal credited modes (both keys carry it), so this cannot change the answer.
    canReuseFilterCounts(attribution, appliedSigs.sources, draftSigs.sources, creditedOnly(draftQuery.attribution));
  const linksFromCards =
    cardsRead &&
    canReuseLinks(attribution, draftQuery.attribution, appliedSigs.links, draftSigs.links, a.conditionsAvailable);
  // The links the link picker lists besides those its counts' links read finds: the brand's, and those the picks name.
  const listedLinks = [
    ...brandLinks.map((l) => l.aff_id),
    ...(linkSel?.exact ?? []),
    ...(linkSel?.allExcept ?? []),
    ...(linkSel?.affiliates ?? []).flatMap((v) => v.exceptLinks),
    ...(draft.links?.exact ?? []),
    ...(draft.links?.allExcept ?? []),
    ...(draft.links?.affiliates ?? []).flatMap((v) => v.exceptLinks),
  ];
  // The aggregate counts; quality mode counts through its facets and asks nothing here.
  const legacySheet = !quality && sheetOpen;
  const counts = useFilterCounts({
    ...countsBase,
    // The brand's addresses are known: the page loaded, or failed after (its error still offers the filters).
    ready: !quality && (a.phase === 'ready' || a.phase === 'error') && a.hosts.length > 0,
    pages: draftQuery.pages,
    pagesOut: draftQuery.pagesOut,
    attribution: draftQuery.attribution,
    countries: draftQuery.countries,
    // The cards' links are listed too while they stand in for the picker's.
    listedLinks: linksFromCards ? [...(a.links.data ?? []).map((r) => r.affId), ...listedLinks] : listedLinks,
    want: {
      countries: legacySheet && shownSection === 'country',
      affiliates: legacySheet && shownSection === 'link' ? affiliateValues : NONE_WANTED,
      campaigns: legacySheet && shownSection === 'campaign' ? campaignValues : NONE_WANTED,
      sources: legacySheet && shownSection === 'source' && !sourcesFromCards,
      links: legacySheet && shownSection === 'link' && !linksFromCards,
    },
  });
  const sourcePicker = sourcesFromCards ? a : counts;
  const linkPicker = linksFromCards ? a : counts;
  const pickerAttribution = draftQuery.attribution;
  // A choice's count once asked for (none before its section opens).
  const choiceMeta = React.useCallback(
    (count: ChoiceCount | undefined): React.ReactNode => (count ? countMeta(count.status, count.visitors) : undefined),
    [countMeta],
  );
  // A quality picker's counts wait for the report to settle; with no address (or a failed report and nothing held)
  // there is nothing to count, so they read `-`.
  const pickersWaiting = quality && settledHosts === null && qr.status !== 'error';
  const facetStatus = React.useCallback(
    (slot: FacetSlot<unknown>): 'loading' | 'error' | 'ready' =>
      slot.status === 'idle' ? (pickersWaiting ? 'loading' : 'error') : slot.status,
    [pickersWaiting],
  );

  // Every source, each followed by the platforms it names (and those picked, if others). The others that sent
  // visits in the period (another website's address, an email or ad's utm_source) are found by the search only, so the
  // list never grows as they load.
  // Each choice counts what picking it shows: its arrivals (within the picked link), or its credited visitors where no
  // arrival can name it (Unknown, or with "no link" picked).
  const sourceOptions = React.useMemo(() => {
    const waitingHere = waitingForData(tenantId, a.phase);
    // Quality mode reads both counts from the source facet (null where the service could not give one).
    const facet =
      quality && facets.sources.data
        ? qualitySourceCounts(facets.sources.data.rows, facets.sources.data.channelArrivals)
        : null;
    const reach: SourceReach = facet
      ? { channels: [], platforms: facet.reachRows }
      : sourcePicker.sourceReach.data ?? { channels: [], platforms: [] };
    const credited = facet ? facet.creditedRows : sourcePicker.sources.data ?? [];
    const creditedChannels: ReadonlyMap<Channel, number | null> =
      facet?.creditedChannels ?? new Map(channelTotals(credited).map((c) => [c.channel, c.visitors]));
    const reachChannels: ReadonlyMap<Channel, number | null> =
      facet?.reachChannels ?? new Map(reach.channels.map((c) => [c.channel, c.visitors]));
    const platformsOf = (rows: SourceRow[]): Map<string, number> =>
      new Map(rows.map((r) => [platformValue(r.channel, r.platform), r.visitors]));
    const creditedPlatforms: ReadonlyMap<string, number | null> = facet?.credited ?? platformsOf(credited);
    const reachPlatforms: ReadonlyMap<string, number | null> = facet?.reach ?? platformsOf(reach.platforms);
    // A cut facet list may leave out a choice's row; the channels' arrivals are whole totals, their credits are not.
    const cut = facet !== null && (facets.sources.data?.truncated ?? false);
    // Picking a source shows its arrivals unless the links picked are credited only (no arrival names Unknown).
    const arrives = (c: Channel): boolean => c !== 'Unknown' && !creditedOnly(pickerAttribution);
    const glyph = (name: ShareGlyphName): React.ReactNode => (
      <OptionGlyph>
        <ShareGlyph name={name} size={18} />
      </OptionGlyph>
    );
    return [
      { value: ALL, label: t('All sources') },
      ...CHANNELS.flatMap((c) => {
        const status =
          draftQuery.pages.length > 0 && !arrives(c)
            ? 'unavailable'
            : quality
              ? facetStatus(facets.sources)
              : waitingHere
                ? 'loading'
                : arrives(c)
                  ? sourcePicker.sourceReach.status
                  : sourcePicker.sources.status;
        const rows = arrives(c) ? reach.platforms : credited;
        const byChannel = arrives(c) ? reachChannels : creditedChannels;
        const byPlatform = arrives(c) ? reachPlatforms : creditedPlatforms;
        const known = Object.hasOwn(PLATFORMS, c) ? PLATFORMS[c] ?? [] : [];
        // Platforms picked from the search (another website, a utm_source) keep their place in the list.
        const picked = [
          ...new Set(
            [...(sourceSel ?? []), ...(draft.channels ?? [])]
              .filter((p) => p.channel === c)
              .flatMap((p) => (p.platforms === 'all' ? p.except : p.platforms)),
          ),
        ].filter((key) => !known.includes(key));
        const seen = drillsDown(c)
          ? rows
              .filter((r) => r.channel === c && r.platform !== '' && !known.includes(r.platform))
              .filter((r) => !picked.includes(r.platform))
              .sort((x, y) => y.visitors - x.visitors)
              .map((r) => r.platform)
          : [];
        const platformOption = (key: string, searchOnly: boolean): IntervalOption<string> => ({
          value: platformValue(c, key),
          label: platformName(key) || t('Unknown'),
          nested: true,
          searchOnly,
          icon: glyph(platformGlyph(key) ?? SOURCE_GLYPHS[c]),
          selectedLabel: `${sourceName(c, t)} › ${platformName(key) || t('Unknown')}`,
          meta: countMeta(status, facetCount(byPlatform, platformValue(c, key), cut)),
        });
        return [
          {
            value: c,
            label: sourceName(c, t),
            icon: glyph(SOURCE_GLYPHS[c]),
            meta: countMeta(status, arrives(c) ? facetCount(byChannel, c, false) : facetCount(byChannel, c, cut, true)),
          },
          ...[...known, ...picked].map((key) => platformOption(key, false)),
          ...seen.map((key) => platformOption(key, true)),
        ];
      }),
    ];
  }, [
    tenantId,
    a.phase,
    quality,
    facets.sources,
    facetStatus,
    sourcePicker.sources,
    sourcePicker.sourceReach,
    sourceSel,
    draft.channels,
    pickerAttribution,
    draftQuery.pages.length,
    t,
    countMeta,
  ]);

  const periodLinks = quality ? linkFacet.map((r) => r.affId) : (linkPicker.links.data ?? []).map((r) => r.affId);
  const linksInTree = [...new Set([...periodLinks, ...listedLinks])].filter(Boolean);
  const affiliateName = (value: string): string =>
    isOwnerTracked(value)
      ? t('Owner-tracked links')
      : directory.affiliates.find((e) => e.value === value)?.name ?? `#${value}`;
  const linkName = (id: string): string =>
    id === '' ? t('No link or unknown link') : brandLinks.find((l) => l.aff_id === id)?.name || id;
  const treeIds = [
    ...new Set([
      ...linksInTree.map(affiliateOf).filter((id): id is string => id !== null),
      ...(draft.links?.affiliates ?? []).map((v) => v.id),
      ...(draft.links?.exceptAffiliates ?? []),
    ]),
  ];
  // Quality mode: a link counts as picking it reads, credited where the picks leave the links credited (no link always
  // is), else by arrival.
  const linksAsCredited = linksCredited(draftQuery.attribution, true);
  const linkCounts: ReadonlyMap<string, number | null> = quality
    ? new Map(linkFacet.map((l) => [l.affId, l.affId === '' || linksAsCredited ? l.creditedPeople : l.arrivalPeople]))
    : new Map((linkPicker.links.data ?? []).map((l) => [l.affId, l.visitors]));
  // A cut link facet may leave out a listed link's row: it reads `-`, never 0.
  const linksCut = quality && (facets.links.data?.truncated ?? false);
  // Each link is counted as picking it reads (see useFilterCounts): unavailable while that is credited and pages are
  // picked.
  const linkStatus = quality
    ? linkRowStatuses(
        draftQuery.pages.length > 0,
        draftQuery.pages.length > 0 && linksAsCredited,
        facetStatus(facets.links),
      )
    : linkRowStatuses(draftQuery.pages.length > 0, counts.linksUnavailable, linkPicker.links.status);
  // An affiliate's or a campaign's badge: its own count, or in quality mode its `link_groups` group.
  const groupMeta = (list: 'affiliates' | 'campaigns', id: string): React.ReactNode =>
    quality
      ? countMeta(facetStatus(facets.linkGroups), badgeCount(facets.linkGroups.data, list, id))
      : choiceMeta((list === 'affiliates' ? counts.affiliates : counts.campaigns).get(id));
  const linkOptions: IntervalOption<string>[] = [
    { value: ALL, label: t('All links') },
    ...(conditions ? [{ value: 'known', label: t('All known links') }] : []),
    {
      value: NO_LINK,
      label: t('No link or unknown link'),
      meta: countMeta(linkStatus.noLink, facetCount(linkCounts, '', linksCut)),
    },
    ...treeIds.flatMap((id) => [
      {
        value: `affiliate:${id}`,
        label: affiliateName(id),
        meta: groupMeta('affiliates', id),
      },
      ...linksInTree
        .filter((l) => affiliateOf(l) === id)
        .map((l) => ({
          value: `exact:${l}`,
          label: linkName(l),
          nested: true,
          meta: countMeta(linkStatus.link, facetCount(linkCounts, l, linksCut)),
        })),
    ]),
    ...linksInTree
      .filter((id) => affiliateOf(id) === null)
      .map((id) => ({
        value: `exact:${id}`,
        label: linkName(id),
        meta: countMeta(linkStatus.link, facetCount(linkCounts, id, linksCut)),
      })),
  ];

  // The presets, and the custom range when one is picked (listed only for the pill's text: it is changed through the
  // custom range row).
  const options = React.useMemo(
    (): IntervalOption<WebsitePeriod>[] => [
      ...WEBSITE_INTERVALS.map((value) => ({ value, label: intervalLabel(value, t, language) })),
      ...(isPreset(interval)
        ? []
        : [{ value: interval, label: intervalLabel(interval, t, language), searchOnly: true }]),
    ],
    [t, language, interval],
  );
  const rangeChoice: CustomRangeChoice = {
    value: isPreset(interval) ? null : { from: interval.slice(0, 10), to: interval.slice(12) },
    max: new Date().toISOString().slice(0, 10),
    maxDays: MAX_RANGE_DAYS,
    onApply: (from, to) => pickInterval(customRange(from, to)),
  };

  // The search also offers any web address typed as a website that sends visitors, so one with no visits in the period
  // yet (or before its first) can be filtered by too.
  const sourceSearch = React.useMemo(
    (): OptionSearch<string> => ({
      placeholder: t('Search sources'),
      empty: t('No sources match your search.'),
      extra: (query) => {
        const host = webHost(query);
        if (!host || sourceOptions.some((o) => o.value === platformValue('Referral', host))) return [];
        const label = `${sourceName('Referral', t)} › ${host}`;
        const icon = (
          <OptionGlyph>
            <ShareGlyph name="link" size={18} />
          </OptionGlyph>
        );
        return [{ value: platformValue('Referral', host), label, icon }];
      },
    }),
    [t, sourceOptions],
  );

  // Each filter's current choice in a few words (the rail, the phone's list, the Source pill, the overview, the PDF).
  const lang = dateLocale(language) ?? 'en-US';
  const selLabel = (sel: Sel, all: string, nameOf: (value: string) => string): string => {
    const applied = appliedSel(sel);
    if (isAll(applied)) return all;
    const names = listOf(applied.values.map(nameOf));
    return applied.mode === 'in' ? names : t('All but {{names}}', { names });
  };
  const campaignName = (value: string): string =>
    directory.campaigns.find((e) => e.value === value)?.name ?? `#${value}`;
  const pageNameOf = (key: string): string => {
    const pg = pageOfKey(key);
    return pg ? pickedOf(pg).name : key;
  };
  const countriesLabel = (sel: Sel): string => selLabel(sel, t('All countries'), (c) => countryName(c, lang));
  const linksLabel = (sel: LinkPick | null): string => {
    if (!sel) return t('All links');
    if (sel.exceptAffiliates?.length)
      return [
        `${t('All known links')} (${t('All but {{names}}', {
          names: listOf([...sel.exceptAffiliates.map(affiliateName), ...(sel.allExcept?.map(linkName) ?? [])]),
        })})`,
        ...(sel.noLink ? [t('No link or unknown link')] : []),
        ...sel.exact.map(linkName),
      ].join(', ');
    if (sel.allExcept !== null)
      return [
        sel.allExcept.length
          ? `${t('All known links')} (${t('All but {{names}}', { names: listOf(sel.allExcept.map(linkName)) })})`
          : t('All known links'),
        ...(sel.noLink ? [t('No link or unknown link')] : []),
      ].join(', ');
    const names = [
      ...sel.affiliates.map((a) =>
        a.exceptLinks.length
          ? `${affiliateName(a.id)} (${t('All but {{names}}', { names: listOf(a.exceptLinks.map(linkName)) })})`
          : affiliateName(a.id),
      ),
      ...sel.exact.map(linkName),
      ...(sel.noLink ? [t('No link or unknown link')] : []),
    ];
    return names.length ? listOf(names) : t('Nothing selected');
  };
  const pagesLabel = (sel: Sel): string => selLabel(sel, t('All pages'), pageNameOf);
  const campaignsLabel = (sel: Sel): string => selLabel(sel, t('All campaigns'), campaignName);
  // Sources group their platforms: "Direct · Search engines › Bing, Yahoo".
  const sourcesLabel = (sel: ChannelPick[] | null): string => {
    if (sel === null) return t('All sources');
    if (!sel.length) return t('Nothing selected');
    return sel
      .map((c) => {
        const name = sourceName(c.channel, t);
        if (c.platforms !== 'all') return `${name}: ${listOf(c.platforms.map(platformName))}`;
        return c.except.length
          ? `${name} (${t('All but {{names}}', { names: listOf(c.except.map(platformName)) })})`
          : name;
      })
      .join(', ');
  };

  // The countries visitors came from in the period, most first, once they are read (and those picked, if none came
  // from them): never the whole world's list, only those with visits.
  // Quality mode: the country facet's rows, a `null` count shown as `-` (and a missing country too, from a cut list).
  const countryCounts = React.useMemo(
    (): {
      status: FacetSlot<unknown>['status'];
      rows: { code: string; visitors: number | null }[];
      truncated: boolean;
    } =>
      quality
        ? {
            status: facets.countries.status,
            rows: (facets.countries.data?.rows ?? []).flatMap((r) => {
              const code = countryCode(r.country);
              return code ? [{ code, visitors: r.people }] : [];
            }),
            truncated: facets.countries.data?.truncated ?? false,
          }
        : { ...counts.countries, truncated: false },
    [quality, facets.countries, counts.countries],
  );
  const countriesLoading = quality
    ? countryCounts.status === 'loading' || (countryCounts.status === 'idle' && pickersWaiting)
    : countryCounts.status === 'idle' || countryCounts.status === 'loading';
  const countryOptions = React.useMemo((): IntervalOption<string>[] => {
    const byCode = new Map(countryCounts.rows.map((r) => [r.code, r.visitors]));
    const ready = countryCounts.status === 'ready';
    const option = (code: string): IntervalOption<string> => ({
      value: code,
      label: countryName(code, lang),
      icon: (
        <OptionGlyph>
          <CountryFlag code={code} />
        </OptionGlyph>
      ),
      ...(ready ? { meta: countMeta('ready', facetCount(byCode, code, countryCounts.truncated)) } : {}),
    });
    const picked = [...countrySel.values, ...draft.countries.values].filter((c) => !byCode.has(c));
    return [
      { value: ALL, label: t('All countries') },
      ...countryCounts.rows.map((r) => option(r.code)),
      ...[...new Set(picked)].map(option),
    ];
  }, [countryCounts, countrySel, draft.countries, lang, t, countMeta]);
  const countriesNote =
    countryCounts.status === 'error'
      ? t('The countries could not be loaded. Close and open the sheet to try again.')
      : countryCounts.status === 'ready' && countryCounts.rows.length === 0
        ? t('No visits with a known country in this period.')
        : null;

  const campaignOptions = React.useMemo((): IntervalOption<string>[] => {
    const named = new Set(directory.campaigns.map((e) => e.value));
    const others = [...campaignSel.values, ...draft.campaigns.values].filter((v) => !named.has(v));
    const meta = (v: string): React.ReactNode =>
      quality
        ? countMeta(facetStatus(facets.linkGroups), badgeCount(facets.linkGroups.data, 'campaigns', v))
        : choiceMeta(counts.campaigns.get(v));
    return [
      { value: ALL, label: t('All campaigns') },
      ...directory.campaigns.map((e) => ({
        value: e.value,
        label: e.name,
        meta: meta(e.value),
      })),
      ...[...new Set(others)].map((v) => ({ value: v, label: `#${v}`, meta: meta(v) })),
    ];
  }, [
    directory.campaigns,
    campaignSel,
    draft.campaigns,
    quality,
    facets.linkGroups,
    facetStatus,
    countMeta,
    counts.campaigns,
    choiceMeta,
    t,
  ]);

  // A links section with nothing to pick (no affiliates platform, no Studio links) is left out once the lists are in.
  const listsPending = directory.status === 'idle' || directory.status === 'loading';
  // The lists could not be read: the filters stay, saying so (not as if the brand had no affiliates or campaigns).
  const listsNote =
    directory.status === 'error'
      ? t('Your affiliates and campaigns could not be loaded. Close and open the sheet to try again.')
      : null;
  // A "-" for a count that cannot be split by page is explained above the choices.
  const unavailableNote = t(w('Visitor counts are unavailable for this selection by page or over time.'));
  const explained = unavailableSections(draftQuery.pages.length > 0, counts.campaigns);
  const sourcesHint = explained.sources ? unavailableNote : null;
  const linksHint = explained.links ? unavailableNote : null;
  // Quality mode: the campaign groups cannot be counted (credited, with pages picked).
  const groupsUnavailable = quality && facets.linkGroups.data !== null && facets.linkGroups.data.campaigns === null;
  const campaignsHint = explained.campaigns || groupsUnavailable ? unavailableNote : null;
  const hasAddresses = tenantId !== undefined && (a.hosts.length > 0 || (latest?.hosts.length ?? 0) > 0);
  // The Filters sheet's Addresses (quality mode): the draft's choice among the brand's kept hosts.
  const addresses: AddressSection | null = quality
    ? addressSection({ selection: draft.hosts, catalogue: a.hosts, latest })
    : null;
  const exposureName = (exposure: QualityExposure): string =>
    exposure === 'public' ? t('Public') : exposure === 'internal' ? t('Internal') : t('Unclassified');
  const addressRail = (rail: string): string =>
    rail === 'All public addresses'
      ? t('All public addresses')
      : rail === 'All internal addresses'
        ? t('All internal addresses')
        : rail === 'Nothing selected'
          ? t('Nothing selected')
          : rail;
  const addressTicks = new Map<string, TickState>();
  const addressOptions: IntervalOption<string>[] = (addresses?.groups ?? []).flatMap((g) => {
    if (g.exposure) addressTicks.set(`${GROUP_PREFIX}${g.exposure}`, g.tick);
    g.rows.forEach((r) => addressTicks.set(`${HOST_PREFIX}${r.host}`, r.tick));
    return [
      ...(g.exposure ? [{ value: `${GROUP_PREFIX}${g.exposure}`, label: exposureName(g.exposure) }] : []),
      ...g.rows.map((r) => ({
        value: `${HOST_PREFIX}${r.host}`,
        label: r.host,
        nested: g.exposure !== null,
        meta: r.kindLabel === 'Landing pages' ? t('Landing pages') : t('Website'),
      })),
    ];
  });
  const sourceTick = (v: string): TickState => {
    if (v === ALL) return draft.channels === null ? 'on' : 'off';
    const at = v.indexOf(PLATFORM_SEP);
    return at < 0
      ? channelTick(draft.channels, v)
      : platformTick(draft.channels, v.slice(0, at), v.slice(at + PLATFORM_SEP.length));
  };
  const sheetSections: FilterSection[] = [
    ...(addresses?.shown
      ? [
          {
            key: ADDRESSES,
            applied: draft.hosts.kind !== 'default',
            title: t('Addresses'),
            valueLabel: addressRail(addresses.rail),
            tick: (v: string): TickState => addressTicks.get(v) ?? 'off',
            options: addressOptions,
            // The list cannot be read: no address can be picked until it can.
            ...(addresses.unavailable
              ? { note: t('Your address list could not be read. Try again in a few minutes.') }
              : {}),
          },
        ]
      : []),
    ...(hasAddresses && tenantId !== undefined
      ? [
          {
            key: 'page',
            applied: narrows(draft.pages),
            title: t('Page'),
            valueLabel: pagesLabel(draft.pages),
            content: (
              <PageList
                tenantId={tenantId}
                hosts={a.hosts}
                span={a.window.current}
                landingPages={landingPages}
                selected={pageScopes}
                tick={(pg) => (pg ? tickOf(draft.pages, pageKey(pg)) : allTick(draft.pages))}
                stickySearch
                quality={
                  quality
                    ? {
                        selectedHosts: settledHosts,
                        range: qualityRange,
                        // A failed first report has no addresses to count: the list shows its error, not loading.
                        failed: qr.status === 'error' && !qr.report,
                      }
                    : undefined
                }
                onPick={(pg) =>
                  setDraft((d) => ({
                    ...d,
                    pages: pg ? toggleValue(d.pages, pageKey({ kind: pg.kind, path: pg.path })) : toggleAllSel(d.pages),
                  }))
                }
              />
            ),
          },
        ]
      : []),
    ...(hasAddresses
      ? [
          {
            key: 'country',
            applied: narrows(draft.countries),
            title: t('Country'),
            valueLabel: countriesLabel(draft.countries),
            tick: (v: string) => flatTick(draft.countries, v),
            options: countryOptions,
            loading: countriesLoading,
            ...(countriesNote ? { note: countriesNote } : {}),
            search: { placeholder: t('Search countries'), empty: t('No countries match your search.') },
          },
        ]
      : []),
    ...(a.attributionAvailable && (phone || sheetKey === 'source')
      ? [
          {
            key: 'source',
            applied: draft.channels !== null,
            title: t('Source'),
            valueLabel: sourcesLabel(draft.channels),
            tick: sourceTick,
            options: sourceOptions,
            ...(sourcesHint ? { hint: sourcesHint } : {}),
            search: sourceSearch,
          },
        ]
      : []),
    ...(a.attributionAvailable && (listsPending || listsNote || campaignOptions.length > 1)
      ? [
          {
            key: 'campaign',
            applied: narrows(draft.campaigns),
            title: t('Campaign'),
            valueLabel: campaignsLabel(draft.campaigns),
            tick: (v: string) => flatTick(draft.campaigns, v),
            options: campaignOptions,
            ...(listsNote ? { note: listsNote } : {}),
            ...(campaignsHint ? { hint: campaignsHint } : {}),
            search: { placeholder: t('Search campaigns'), empty: t('No campaigns match your search.') },
          },
        ]
      : []),
    ...(a.attributionAvailable
      ? [
          {
            key: 'link',
            applied: draft.links !== null,
            title: t('Tracking link'),
            valueLabel: linksLabel(draft.links),
            tick: (v: string): TickState =>
              v === ALL
                ? draft.links === null
                  ? 'on'
                  : 'off'
                : v === 'known'
                  ? draft.links?.allExcept !== null && draft.links?.allExcept !== undefined
                    ? draft.links.allExcept.length || draft.links.exceptAffiliates?.length
                      ? 'mixed'
                      : 'on'
                    : 'off'
                  : v.startsWith('affiliate:')
                    ? affiliateTick(
                        draft.links,
                        v.slice(10),
                        linksInTree.filter((l) => affiliateOf(l) === v.slice(10)),
                      )
                    : linkTick(draft.links, v === NO_LINK ? '' : v.slice(6)),
            options: linkOptions,
            ...(linksHint ? { hint: linksHint } : {}),
          },
        ]
      : []),
  ];
  const pickInSheet = (section: string, value: string): void => {
    let next = draft;
    if (section === ADDRESSES) {
      const group = value.startsWith(GROUP_PREFIX) ? value.slice(GROUP_PREFIX.length) : null;
      if (group !== null && !isExposure(group)) return;
      next = {
        ...draft,
        hosts: toggleAddress(
          draft.hosts,
          group !== null ? { group } : { host: value.slice(HOST_PREFIX.length) },
          latest?.hosts ?? [],
          latest?.hostCatalog.defaultBasis ?? 'none',
        ),
      };
    } else if (section === 'country') next = { ...draft, countries: flatToggle(draft.countries, value) };
    else if (section === 'campaign')
      next = {
        ...draft,
        links: null,
        campaigns: conditions
          ? flatToggle(draft.campaigns, value)
          : value === ALL
            ? ALL_SEL
            : { mode: 'in', values: [value] },
      };
    else if (section === 'source') {
      if (value === ALL) next = { ...draft, channels: draft.channels === null ? [] : null };
      else {
        const [channel, key] = value.split(PLATFORM_SEP);
        if (!isChannel(channel)) return;
        const current = conditions ? draft.channels : [];
        next = { ...draft, channels: key ? togglePlatform(current, channel, key) : toggleChannel(current, channel) };
      }
    } else if (section === 'link') {
      const current = conditions ? draft.links : null;
      const links =
        value === ALL
          ? draft.links === null
            ? emptyLinks()
            : null
          : value === 'known'
            ? {
                ...emptyLinks(),
                noLink: !!current?.noLink,
                allExcept: current?.allExcept?.length === 0 && !current.exceptAffiliates?.length ? null : [],
              }
            : value.startsWith('affiliate:')
              ? toggleAffiliate(
                  current,
                  value.slice(10),
                  linksInTree.filter((l) => affiliateOf(l) === value.slice(10)),
                )
              : toggleLink(current, value === NO_LINK ? '' : value.slice(6));
      next = { ...draft, links, campaigns: ALL_SEL };
    }
    // The aggregate route's own limits; Analytics checks a quality request itself (and says when it is too large).
    const issue = quality ? null : selectionIssue(queryOf(next).attribution, a.conditionsAvailable);
    if (issue) {
      setFilterMessage(
        issue === 'limit'
          ? t('Too many filter conditions. Remove a choice to continue.')
          : t('This environment supports one choice per filter.'),
      );
      return;
    }
    setFilterMessage(null);
    setDraft(next);
  };

  // Export as PDF: a light A4 document built from the figures on screen (see report-pdf), downloaded at once. The
  // report is built where the page works its figures out (below); the button only asks for it.
  const buildReport = React.useRef<() => WebsiteReport | null>(() => null);
  // The report's head carries the studio's light lockup, as the sign-in page shows it (Newton while showcased).
  const newtonShowcase = useAppSelector(selectNShowcase);
  // A click queues the export: a snackbar says it is being prepared until every card has settled (loaded, or failed,
  // which the document shows as "-"), then the document is built and downloaded, and a toast confirms it.
  const [exportQueued, setExportQueued] = React.useState(false);
  const exportRunning = React.useRef(false);
  const exportToast = useToast();
  const showExportToast = exportToast.show;
  // Quality mode exports the report on screen once it has landed, or the earlier answer to the same request a failed
  // read keeps in view (the PDF then prints that note), so its dates and the filter lines are of one request; with no
  // address counted there is nothing to export.
  const emptyScope = report !== null && report.scope.selectedHosts.length === 0;
  const heldAfterFailure = qr.status === 'error' && qr.held;
  const canExport = quality
    ? tenantId !== undefined && (qr.status === 'loading' || qr.status === 'ready' || heldAfterFailure) && !emptyScope
    : tenantId !== undefined && (a.phase === 'loading' || a.phase === 'ready');
  const allSettled = quality
    ? tenantId !== undefined && (qr.status === 'ready' || heldAfterFailure) && report !== null
    : tenantId !== undefined && a.phase === 'ready' && a.current && !cardsLoading(a);
  // Runs the queued export once the page holds every figure, with the filters as they are by then.
  React.useEffect(() => {
    if (!exportQueued || exportRunning.current) return;
    if (!canExport) {
      setExportQueued(false);
      showExportToast(t('We could not make the PDF. Please try again.'));
      return;
    }
    if (!allSettled) return;
    const report = buildReport.current();
    if (!report) {
      setExportQueued(false);
      return;
    }
    exportRunning.current = true;
    downloadWebsiteReport(report)
      .then(() => showExportToast(t('Your PDF is downloaded.'), { icon: 'check' }))
      .catch(() => showExportToast(t('We could not make the PDF. Please try again.')))
      .finally(() => {
        exportRunning.current = false;
        setExportQueued(false);
      });
  }, [exportQueued, allSettled, canExport, showExportToast, t]);
  const exportPdf = (): void => setExportQueued(true);

  // The sheet's pill only while it has a filter to offer (no page list without the brand's addresses, no source or
  // link without attribution).
  const filtersPill =
    sheetSections.length > 0 ? <FiltersPill count={activeFilters} onClick={() => openSheet()} /> : null;
  const header = (
    <SectionHead
      title={brandName ? t('{{brand}} website') : t('Your website')}
      placeholders={brandName ? { brand: brandName } : undefined}
      first
      sticky
      hideTitleOnMobile
      right={
        phone ? (
          <Controls $wrap>
            {filtersPill}
            <IntervalDropdown value={interval} options={options} onChange={pickInterval} custom={rangeChoice} />
          </Controls>
        ) : (
          <Controls $wrap={a.attributionAvailable}>
            {filtersPill}
            {/* The sources at hand on a wider screen: a shortcut into the sheet's Source (several can be picked). */}
            {a.attributionAvailable ? (
              <ShortcutPill
                label={sourcesLabel(sourceSel)}
                active={sourceSel !== null}
                ariaLabel={t('Change traffic source')}
                onClick={() => openSheet('source')}
              />
            ) : null}
            <IntervalDropdown value={interval} options={options} onChange={pickInterval} custom={rangeChoice} />
          </Controls>
        )
      }
    />
  );

  const sheet = sheetOpen ? (
    <FiltersSheet
      sections={sheetSections}
      onPick={pickInSheet}
      message={
        filterMessage ??
        (facetsTooLarge
          ? t('Too many filter conditions. Remove a choice to continue.')
          : !conditions
            ? t('This environment supports one choice per filter.')
            : undefined)
      }
      canClear={
        draft.channels !== null ||
        draft.links !== null ||
        [draft.pages, draft.countries, draft.campaigns].some((sel) => !isAll(sel)) ||
        (quality && draft.hosts.kind !== 'default')
      }
      onClear={() => {
        setDraft((d) => ({ ...NO_FILTERS, interval: d.interval }));
        setFilterMessage(null);
      }}
      onApply={() =>
        updateFilters({
          pages: appliedSel(draft.pages),
          countries: appliedSel(draft.countries),
          channels: draft.channels,
          links: draft.links,
          campaigns: appliedSel(draft.campaigns),
          // No address ticked writes `hosts=none`: the screen then asks nothing until one is chosen.
          ...(quality ? { hosts: draft.hosts } : {}),
        })
      }
      onClose={() => setSheetOpen(false)}
      onSectionShow={setShownSection}
      initialKey={sheetKey}
    />
  ) : null;

  const notSetUp = (
    <Notice
      title={t('Website stats are not available yet')}
      body={t('We are still setting up visitor stats for your website. Please check back soon.')}
    />
  );
  const noSite = (
    <Notice
      title={t('Your website is not live yet')}
      body={t('Visitor stats show up here once your website is published and people start visiting it.')}
    />
  );
  // A failed quality read, as its relay code says (its words only, never the relay's own text).
  const qualityFailure = (error: QualityScreenState): React.ReactElement => {
    if (error.kind === 'not-set-up') return notSetUp;
    if (error.kind === 'refused')
      return (
        <NoticeFrame role="status">
          <NoticeTitle text={error.text} tag="h3" />
        </NoticeFrame>
      );
    return (
      <Notice
        title={t('We could not load your website stats')}
        body={t(error.kind === 'failed' ? error.body : 'Something went wrong on our side. Please try again.')}
        actionLabel={t('Try again')}
        onAction={qr.reload}
      />
    );
  };
  const chooseAddresses = (): void => openSheet(ADDRESSES);

  let notice: React.ReactElement | null = null;
  if (tenantId === undefined) {
    notice = brandSettled ? (
      <Notice title={t('No brand selected')} body={t('Pick a brand to see how many people visit its website.')} />
    ) : null;
  } else if (quality) {
    if (qr.status === 'no-site') notice = noSite;
    else if (qr.status === 'no-selection')
      notice = (
        <NoticeFrame role="status">
          <NoticeTitle text="No address chosen" tag="h3" />
          {qr.noSelection === 'stale' ? (
            <NoticeBody text="Saved addresses are no longer available; choose addresses." tag="paragraph" />
          ) : (
            <NoticeBody text="Choose at least one address to see its numbers." tag="paragraph" />
          )}
          <Button label={t('Choose addresses')} variant="outline" icon="filter" onClick={chooseAddresses} />
        </NoticeFrame>
      );
    // A failure with earlier figures in view keeps them, with the failure above them (see below).
    else if (qr.status === 'error' && qr.error && (qr.error.kind === 'not-set-up' || qr.report === null))
      notice = qualityFailure(qr.error);
  } else if (a.phase === 'unavailable') {
    notice = notSetUp;
  } else if (a.phase === 'no-site') {
    notice = noSite;
  } else if (a.phase === 'error') {
    notice = (
      <Notice
        title={t('We could not load your website stats')}
        body={a.error ? t(a.error) : t('Something went wrong on our side. Please try again.')}
        actionLabel={t('Try again')}
        onAction={a.reload}
      />
    );
  }
  if (notice) {
    return (
      <Page>
        {header}
        {sheet}
        {/* An export asked for before the page fell back to a notice says here that it could not be made. */}
        <Toast toast={exportToast.toast} />
        {notice}
      </Page>
    );
  }

  // Quality mode: the report's figures (the last report of this brand and mode while a new one is read), every count
  // the service could not give kept apart from 0.
  const view = report ? qualityShown(report) : null;
  const waiting = quality ? view === null : tenantId === undefined || a.phase === 'loading';
  const totalsLoading = waiting || (!quality && a.totals.status === 'loading');
  const caption = comparisonCaption(interval, t);
  const totals: ShownTotals | null = quality ? view?.totals ?? null : a.totals.data;
  const prevTotals: ShownTotals | null = quality
    ? view?.previousTotals ?? null
    : a.previousTotals.status === 'ready'
      ? a.previousTotals.data
      : null;
  const time = quality ? view?.time ?? null : a.time.status === 'ready' ? a.time.data : null;
  const prevTime = quality
    ? view?.previousTime ?? null
    : a.previousTime.status === 'ready'
      ? a.previousTime.data
      : null;
  const narrowed = a.attributionAvailable && attributionActive(attribution);
  // The most visited pages: listed unless pages are picked, or every page but some is asked with more filters than the
  // service takes (the cards leave it out then too; Analytics takes them all in quality mode).
  const pagesListed =
    !onPage &&
    (quality ||
      !pagesOut ||
      fitsFilters(
        topPagesQuery(a.window.current),
        attribution,
        narrowed,
        scopeOf(pageScopes, a.hosts, true),
        query.countries,
        true,
      ));
  // How the people and visits read: through one link or several, one affiliate or campaign (several read as their
  // links), one source or several.
  const sourcePicks = sourceSel;
  const arrivals: Arrivals =
    !narrowed || !countsArrivals(attribution)
      ? null
      : linkSel
        ? linkSel.exact.length === 1 && !linkSel.affiliates.length && linkSel.allExcept === null
          ? 'link'
          : 'links'
        : narrows(campaignSel)
          ? 'campaign'
          : (sourceSel?.length ?? 0) > 1
            ? 'sources'
            : 'source';
  // Quality mode reads what the service could not count from the report itself (its `null` counts).
  const trendVisitorsUnavailable = quality
    ? view !== null && !view.trendVisitors
    : narrowed && !selectionEmpty(attribution) && !countsArrivals(attribution);
  const visitorUnavailable = quality
    ? view !== null && view.totals.visitors === null
    : trendVisitorsUnavailable && pageScopes.length > 0;
  const summary = visitorUnavailable
    ? {
        lead: t('Pages viewed for this selection'),
        follow: [t(w('Visitor counts are unavailable for this selection by page or over time.'))],
      }
    : summaryLines(
        quality ? qualitySummaryInput(totals, prevTotals, time) : a,
        interval,
        t,
        language,
        onPage,
        narrowed,
        arrivals,
        w,
      );
  const summaryFilters: SummaryFilter[] = [];
  if (narrowed && sourceSel !== null) {
    const first = sourceSel[0];
    const onePlatform =
      sourceSel.length === 1 && first.platforms !== 'all' && first.platforms.length === 1 ? first.platforms[0] : null;
    const host = onePlatform ? webHost(onePlatform) : null;
    const site = onePlatform ? platformSite(onePlatform) ?? (host ? `https://${host}` : undefined) : undefined;
    summaryFilters.push({
      key: 'source',
      label: t('Source'),
      name: sourcesLabel(sourceSel),
      glyph: first && Object.hasOwn(SOURCE_GLYPHS, first.channel) ? SOURCE_GLYPHS[first.channel] : 'question',
      ...(site ? { href: site } : {}),
    });
  }
  if (narrowed && linkSel !== null) {
    const one =
      !linkSel.affiliates.length && !linkSel.noLink && linkSel.allExcept === null && linkSel.exact.length === 1
        ? linkSel.exact[0]
        : '';
    const tracked = one ? brandLinks.find((l) => l.aff_id === one) : undefined;
    summaryFilters.push({
      key: 'link',
      label: t('Tracking links'),
      name: linksLabel(linkSel),
      glyph: 'link',
      ...(tracked && assetKindOpens(tracked.kind, isEnabled)
        ? { onOpen: () => assetOverlay.open(tracked.kind, tracked.creative_id) }
        : {}),
      ...(tracked?.url ? { url: tracked.url } : {}),
    });
  }
  if (narrowed && narrows(campaignSel))
    summaryFilters.push({ key: 'campaign', label: t('Campaign'), name: campaignsLabel(campaignSel), glyph: 'link' });
  if (narrows(countrySel)) {
    const picks = appliedSel(countrySel);
    const names = listOf(picks.values.map((c) => countryName(c, lang)));
    const one = picks.mode === 'in' && picks.values.length === 1 ? picks.values[0] : null;
    summaryFilters.push({
      key: 'country',
      label: picks.values.length > 1 || picks.mode === 'out' ? t('Countries') : t('Country'),
      name: picks.mode === 'in' ? names : t('All but {{names}}', { names }),
      glyph: 'globe',
      // One country: its flag stands for it.
      ...(one ? { picture: <CountryFlag code={one} size={40} /> } : {}),
    });
  }

  const pages = quality ? view?.pages ?? [] : a.pages.data ?? [];
  const maxViews = Math.max(1, ...pages.map((p) => p.views));
  // A page's live address: a landing page's own (its path on the brand's lp. host), else the website's. The website's
  // main host is its shortest site host (its own domain before the internal *.sites.… one).
  const siteHost = a.hosts
    .filter((h) => h.kind === 'site')
    .map((h) => h.host)
    .sort((x, y) => x.length - y.length)[0];
  const pageUrl = (path: string): string | undefined => {
    const bare = path.replace(/\/+$/, '') || '/';
    const landingPage = landingPages.find((p) => (p.path.replace(/\/+$/, '') || '/') === bare);
    if (landingPage?.url) return /^https?:\/\//i.test(landingPage.url) ? landingPage.url : undefined;
    // A path comes from the visits' events: only one that stays on the website makes an address (`@evil.example` or
    // `//evil.example` would leave it).
    if (!siteHost || !path.startsWith('/')) return undefined;
    try {
      const url = new URL(path, `https://${siteHost}`);
      return url.host === siteHost ? url.href : undefined;
    } catch {
      return undefined;
    }
  };
  // With a source, a link or a country picked, the overview also names the pages they are narrowed to.
  if (summaryFilters.length > 0 && narrows(pageSel)) {
    const href = picked ? pageUrl(picked.path) : undefined;
    summaryFilters.push({
      key: 'page',
      label: picked ? (picked.kind === 'landing' ? t('Landing page') : t('Page')) : t('Pages'),
      name: pagesLabel(pageSel),
      ...(href ? { href } : {}),
    });
  }
  const pageItems: RankedItem[] = pages.map((p) => {
    const name = pageName(p.path, t);
    const href = pageUrl(p.path);
    return {
      key: p.path,
      name,
      sub: p.path,
      ...(href ? { href } : {}),
      value: viewsText(p.views, t, language),
      note: p.avgSeconds !== null ? t('{{time}} on average', { time: formatDuration(p.avgSeconds, t) }) : undefined,
      share: p.views / maxViews,
      change: p.previousViews === null ? null : periodDelta(p.views, p.previousViews),
    };
  });

  // The unavailable line stands in for a split the service could not count by person in quality mode.
  const unavailableLine = t(w('Visitor counts are unavailable for this selection by page or over time.'));
  const reachWord = t('{{n}} reach', { n: '' }).trim();
  const reachLabel = `${reachWord.charAt(0).toLocaleUpperCase(language || undefined)}${reachWord.slice(1)}`;
  const devicesUnavailable = quality && view !== null && (view.devices === null || view.totals.visitors === null);
  const systemsUnavailable = quality && view !== null && (view.systems === null || view.totals.visitors === null);
  const devices = quality ? (devicesUnavailable ? [] : view?.devices ?? []) : a.devices.data ?? [];
  const deviceTotal = quality ? view?.totals.visitors ?? 0 : devices.reduce((sum, d) => sum + d.visitors, 0);
  const devicesBefore = quality
    ? view?.previousDevices ?? null
    : a.previousDevices.status === 'ready'
      ? a.previousDevices.data ?? []
      : null;
  const deviceTiles: ShareTile[] = devices.map((d) => ({
    key: d.kind,
    glyph: DEVICE_GLYPHS[d.kind],
    name: deviceName(d.kind, t),
    value: quality
      ? t('{{n}} reach', { n: deviceTotal > 0 ? percentOf(d.visitors, deviceTotal) : '0%' })
      : percentOf(d.visitors, deviceTotal),
    note: visitorsText(d.visitors, t, language, quality),
    count: d.visitors,
    share: deviceTotal > 0 ? d.visitors / deviceTotal : 0,
    change: devicesBefore ? periodDelta(d.visitors, devicesBefore.find((b) => b.kind === d.kind)?.visitors ?? 0) : null,
  }));

  const systems = quality ? (systemsUnavailable ? [] : view?.systems ?? []) : a.systems.data ?? [];
  const systemTotal = quality ? view?.totals.visitors ?? 0 : systems.reduce((sum, d) => sum + d.visitors, 0);
  const systemsBefore = quality
    ? view?.previousSystems ?? null
    : a.previousSystems.status === 'ready'
      ? a.previousSystems.data ?? []
      : null;
  const systemTiles: ShareTile[] = systems.map((d) => ({
    key: d.kind,
    glyph: SYSTEM_GLYPHS[d.kind],
    name: systemName(d.kind, t),
    value: quality
      ? t('{{n}} reach', { n: systemTotal > 0 ? percentOf(d.visitors, systemTotal) : '0%' })
      : percentOf(d.visitors, systemTotal),
    note: visitorsText(d.visitors, t, language, quality),
    count: d.visitors,
    share: systemTotal > 0 ? d.visitors / systemTotal : 0,
    change: systemsBefore ? periodDelta(d.visitors, systemsBefore.find((b) => b.kind === d.kind)?.visitors ?? 0) : null,
  }));

  // Addresses only matter when the brand has more than one (its site plus landing pages, or several domains); in quality
  // mode, when more than one is counted.
  const showAddresses = quality ? (report?.scope.selectedHosts.length ?? 0) > 1 : a.hosts.length > 1;
  const hostsUnavailable = quality && view !== null && view.hosts === null;
  const visitorsByHost = new Map((a.sites.data ?? []).map((h) => [h.host, h.visitors]));
  const hostsBefore =
    a.previousSites.status === 'ready' ? new Map((a.previousSites.data ?? []).map((h) => [h.host, h.visitors])) : null;
  // Each address with its visitors, and those of the period before (null: not compared), most first.
  const hostRows: { host: string; kind: HostKind; visitors: number; previous: number | null }[] = (
    quality
      ? view?.hosts ?? []
      : a.hosts.map((h) => ({
          host: h.host,
          kind: h.kind,
          visitors: visitorsByHost.get(h.host) ?? 0,
          previous: hostsBefore ? hostsBefore.get(h.host) ?? 0 : null,
        }))
  ).sort((x, y) => y.visitors - x.visitors);
  const maxHost = Math.max(1, ...hostRows.map((h) => h.visitors));
  const hostItems: RankedItem[] = hostRows.map(({ host, kind, visitors, previous }) => ({
    key: host,
    name: host,
    sub: hostKindName(kind, t),
    value: visitorsText(visitors, t, language, quality),
    share: visitors / maxHost,
    change: previous === null ? null : periodDelta(visitors, previous),
  }));

  // Where the visitors came from: each counted once, under the source credited with their visit; with one source picked
  // (one that names platforms), its platforms one by one, the smallest summed into one tile. The card reads every
  // source (the source picker reuses the read) and keeps here, row by row, the sources and platforms picked (exactly,
  // within each picked source and its platforms).
  const keepsSource = (r: SourceRow): boolean =>
    sourcePicks === null || platformTick(sourcePicks, r.channel, r.platform) === 'on';
  const sourceRows = (a.sources.data ?? []).filter(keepsSource);
  const sourceRowsBefore =
    a.previousSources.status === 'ready' ? (a.previousSources.data ?? []).filter(keepsSource) : null;
  const oneSource = sourcePicks?.length === 1 ? sourcePicks[0].channel : null;
  const drill = drillsDown(oneSource) ? oneSource : null;
  // Quality mode: the report's source card (its channels, or the one picked source's top rows and the rest as Other).
  const qualitySources = report ? sourceCard(report, drill) : null;
  const sourcesUnavailable = qualitySources?.kind === 'unavailable';
  let sourceTiles: ShareTile[];
  if (quality) {
    const card = qualitySources?.kind === 'tiles' ? qualitySources : null;
    // Several sources picked: the card keeps the picked ones, as the source filter reads.
    const kept = (card?.tiles ?? []).filter(
      (tile) => drill !== null || sourcePicks === null || channelTick(sourcePicks, tile.channel) !== 'off',
    );
    const other = card?.other ?? 0;
    const total = kept.reduce((sum, tile) => sum + tile.visitors, 0) + other;
    const previousOf = (tile: SourceTile): number | null => {
      if (!report) return null;
      const before =
        tile.platform === null
          ? report.previous.channels.find((c) => c.channel === tile.channel)
          : report.previous.sources.find((r) => r.channel === tile.channel && r.platform === tile.platform);
      return before ? before.creditedPeople : 0;
    };
    sourceTiles = kept.map((tile) => {
      const before = previousOf(tile);
      return {
        key: tile.platform === null ? tile.channel : tile.platform,
        glyph:
          tile.platform === null
            ? SOURCE_GLYPHS[tile.channel]
            : platformGlyph(tile.platform) ?? SOURCE_GLYPHS[tile.channel],
        name: tile.platform === null ? sourceName(tile.channel, t) : platformName(tile.platform) || t('Unknown'),
        value: percentOf(tile.visitors, total),
        note: visitorsText(tile.visitors, t, language, true),
        count: tile.visitors,
        share: tile.visitors / Math.max(1, total),
        change: before === null ? null : periodDelta(tile.visitors, before),
      };
    });
    if (other > 0)
      sourceTiles.push({
        key: OTHER_TILE,
        glyph: 'system',
        name: t('Other'),
        value: percentOf(other, total),
        note: visitorsText(other, t, language, true),
        count: other,
        share: other / Math.max(1, total),
        change: null,
      });
  } else if (drill) {
    const rows = sourceRows.filter((r) => r.channel === drill).sort((x, y) => y.visitors - x.visitors);
    const total = rows.reduce((sum, r) => sum + r.visitors, 0);
    const before = sourceRowsBefore?.filter((r) => r.channel === drill) ?? null;
    const shown = rows.length > TOP_PLATFORMS + 1 ? rows.slice(0, TOP_PLATFORMS) : rows;
    const rest = rows.slice(shown.length);
    sourceTiles = shown.map((r) => ({
      key: r.platform,
      glyph: platformGlyph(r.platform) ?? SOURCE_GLYPHS[drill],
      name: platformName(r.platform) || t('Unknown'),
      value: percentOf(r.visitors, total),
      note: visitorsText(r.visitors, t, language),
      count: r.visitors,
      share: r.visitors / Math.max(1, total),
      change: before ? periodDelta(r.visitors, before.find((b) => b.platform === r.platform)?.visitors ?? 0) : null,
    }));
    if (rest.length > 0) {
      const restVisitors = rest.reduce((sum, r) => sum + r.visitors, 0);
      sourceTiles.push({
        key: OTHER_TILE,
        glyph: 'system',
        name: t('Other'),
        value: percentOf(restVisitors, total),
        note: visitorsText(restVisitors, t, language),
        count: restVisitors,
        share: restVisitors / Math.max(1, total),
        change: null,
      });
    }
  } else {
    const channels = channelTotals(sourceRows);
    const total = channels.reduce((sum, c) => sum + c.visitors, 0);
    const before = sourceRowsBefore ? channelTotals(sourceRowsBefore) : null;
    sourceTiles = channels.map((c) => ({
      key: c.channel,
      glyph: SOURCE_GLYPHS[c.channel],
      name: sourceName(c.channel, t),
      value: percentOf(c.visitors, total),
      note: visitorsText(c.visitors, t, language),
      count: c.visitors,
      share: c.visitors / Math.max(1, total),
      change: before ? periodDelta(c.visitors, before.find((b) => b.channel === c.channel)?.visitors ?? 0) : null,
    }));
  }
  // The card follows the source filter (no picking here, like the other cards): a source's platforms once one is
  // picked in the source pill.
  // The card credits each visitor once, so with a country picked it can only place them by the country of their latest
  // visit (the other cards count every visit from that country).
  // Quality mode: some source names were too small or too long to list, so they count only in Other.
  const sourcesCut = qualitySources?.kind === 'tiles' && qualitySources.cut;
  const sourcesNote = drill
    ? sourcesCut
      ? t('{{source}}, one by one. Some small or very long source names count only in Other.', {
          source: sourceName(drill, t),
        })
      : t('{{source}}, one by one.', { source: sourceName(drill, t) })
    : narrows(countrySel)
      ? t(w('Each visitor counts once, under the source that last brought them, by the country of their latest visit.'))
      : t(w('Each visitor counts once, under the source that last brought them.'));
  const sourcesCard = a.attributionAvailable ? (
    <StatCard title={t(w('Where visitors come from'))} sub={sourcesNote}>
      <ShareTiles
        items={sourceTiles}
        loading={waiting || (!quality && a.sources.status === 'loading')}
        error={!quality && a.sources.status === 'error'}
        emptyText={sourcesUnavailable ? unavailableLine : t('No visits in this period yet.')}
        errorText={t('We could not load this. Try again in a moment.')}
        placeholderTiles={3}
      />
    </StatCard>
  ) : null;

  // What people use: the kind of device, then the system it runs, on one card.
  const devicesCard = (
    <StatCard
      title={t('Phone or computer?')}
      sub={
        onPage === 'pages'
          ? t('What people use to open these pages')
          : onPage
            ? t('What people use to open this page')
            : t('What people use to open your website')
      }
    >
      <ShareTiles
        items={deviceTiles}
        loading={waiting || (!quality && a.devices.status === 'loading')}
        error={!quality && a.devices.status === 'error'}
        emptyText={devicesUnavailable ? unavailableLine : t('No visits in this period yet.')}
        errorText={t('We could not load this. Try again in a moment.')}
        placeholderTiles={2}
      />
      <CardDivider variant="styled" />
      <CardSection>
        <CardSectionTitle text="Which system?" tag="h3" />
        <CardSectionSub text="The software their phone or computer runs" tag="span" />
      </CardSection>
      <ShareTiles
        items={systemTiles}
        loading={waiting || (!quality && a.systems.status === 'loading')}
        error={!quality && a.systems.status === 'error'}
        emptyText={systemsUnavailable ? unavailableLine : t('No visits in this period yet.')}
        errorText={t('We could not load this. Try again in a moment.')}
        placeholderTiles={3}
      />
      {quality ? <CardSectionSub text="A person can appear in more than one row." tag="span" /> : null}
    </StatCard>
  );

  const footnote = quality
    ? qualityFootnote(report?.scope.filterBasis.people ?? 'none')
        .map((key) => t(key))
        .join(' ')
    : t(
        'A visitor is one phone or computer, so someone who visits on two devices counts twice. Numbers update every few minutes.',
      );

  // The same figures as a document (the Export as PDF button).
  buildReport.current = (): WebsiteReport => {
    const dayFmt = new Intl.DateTimeFormat(language || undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const fullFmt = new Intl.DateTimeFormat(language || undefined, { dateStyle: 'medium', timeZone: 'UTC' });
    const hourFmt = new Intl.DateTimeFormat(language || undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: 'UTC',
    });
    const span = a.window.current;
    // A custom range of two days is charted by the hour: its hours name their day.
    const twoDays = span.to - span.from > 24 * 60 * 60 * 1000;
    const days = `${fullFmt.format(span.from)} - ${fullFmt.format(Math.max(span.from, span.to - 1))}`;
    const label = intervalLabel(interval, t, language);
    const title = brandName ? t('{{brand}} website', { brand: brandName }) : t('Your website');
    const changeText = (c: StatsDelta | null | undefined): string => c?.label ?? '-';
    const timeText = (seconds: number | null): string => {
      const f = timeFigure(seconds, t);
      return f ? `${f.value}${f.suffix ?? ''}` : '-';
    };
    const tables: ReportTable[] = [];
    if (pagesListed) {
      tables.push({
        title: t('Most visited pages'),
        sub: t('The pages people open most, and how long they stay'),
        columns: [
          { label: t('Page'), width: 0.3 },
          { label: t('Address'), width: 0.3 },
          { label: t('Views'), align: 'right' },
          { label: t('vs before'), align: 'right' },
          { label: t('Time on page'), align: 'right' },
        ],
        rows: pages.map((p) => [
          pageName(p.path, t),
          p.path,
          formatCount(p.views, language),
          p.previousViews === null ? '-' : changeText(periodDelta(p.views, p.previousViews)),
          p.avgSeconds !== null ? formatDuration(p.avgSeconds, t) : '-',
        ]),
        links: { column: 1, urls: pages.map((p) => pageUrl(p.path) ?? null) },
        empty: t('No pages visited in this period yet.'),
      });
    }
    // A split the service could not count by person says so, as the card does.
    const shareTable = (
      tableTitle: string,
      sub: string,
      name: string,
      tiles: ShareTile[],
      unavailable = false,
      valueLabel = t('Share'),
    ): ReportTable => ({
      title: tableTitle,
      sub,
      columns: [
        { label: name, width: 0.45 },
        { label: t(w('Visitors')), align: 'right' },
        { label: valueLabel, align: 'right' },
        { label: t('vs before'), align: 'right' },
      ],
      rows: tiles.map((tile) => [
        tile.name,
        tile.count !== undefined ? formatCount(tile.count, language) : '-',
        tile.value,
        changeText(tile.change),
      ]),
      empty: unavailable ? unavailableLine : t('No visits in this period yet.'),
    });
    if (a.attributionAvailable) {
      tables.push(
        shareTable(t(w('Where visitors come from')), sourcesNote, t('Source'), sourceTiles, sourcesUnavailable),
      );
    }
    tables.push(
      shareTable(
        t('Phone or computer?'),
        onPage === 'pages'
          ? t('What people use to open these pages')
          : onPage
            ? t('What people use to open this page')
            : t('What people use to open your website'),
        t('Device'),
        deviceTiles,
        devicesUnavailable,
        reachLabel,
      ),
    );
    tables.push(
      shareTable(
        t('Which system?'),
        t('The software their phone or computer runs'),
        t('System'),
        systemTiles,
        systemsUnavailable,
        reachLabel,
      ),
    );
    if (!onPage && showAddresses) {
      tables.push({
        title: t('Website and landing pages'),
        sub: t(w('Visitors on each of your addresses')),
        columns: [
          { label: t('Address'), width: 0.45 },
          { label: t(w('Visitors')), align: 'right' },
          { label: t('vs before'), align: 'right' },
        ],
        rows: hostRows.map(({ host, kind, visitors, previous }) => [
          `${host} (${hostKindName(kind, t)})`,
          formatCount(visitors, language),
          previous === null ? '-' : changeText(periodDelta(visitors, previous)),
        ]),
        empty: hostsUnavailable ? unavailableLine : t('No visits in this period yet.'),
      });
    }
    const trend = a.trend.data ?? [];
    const filterLines = [
      ...(narrows(pageSel) ? [{ label: picked ? t('Page') : t('Pages'), value: pagesLabel(pageSel) }] : []),
      // The overview's own page entry is the one above.
      ...summaryFilters.filter((f) => f.key !== 'page').map((f) => ({ label: f.label, value: f.name })),
    ];
    const doc: WebsiteReport = {
      logo: newtonShowcase
        ? { src: NEWTON_LOGO_LIGHT_SRC, alt: 'Newton' }
        : { src: WHITELABELS_LOGO_LIGHT_SRC, alt: 'Whitelabels.com' },
      ...(brandLogoForLight(displayBrand?.casino_data?.details?.logo)
        ? { brandLogo: brandLogoForLight(displayBrand?.casino_data?.details?.logo) }
        : {}),
      title,
      kind: t('Website analytics'),
      exported: t('Exported {{date}}', { date: fullFmt.format(Date.now()) }),
      period: isPreset(interval) ? `${label} (${days})` : label,
      filters: filterLines,
      lead: summary.lead,
      follow: summary.follow,
      kpis: [
        {
          label: t('Visitors'),
          value:
            totals && !visitorUnavailable && totals.visitors !== null ? formatCount(totals.visitors, language) : '-',
          ...kpiChange(changeOf(visitorUnavailable ? null : totals?.visitors, prevTotals?.visitors)),
          caption,
        },
        {
          label: t('Visits'),
          value: totals && !visitorUnavailable && totals.visits !== null ? formatCount(totals.visits, language) : '-',
          ...kpiChange(changeOf(visitorUnavailable ? null : totals?.visits, prevTotals?.visits)),
          caption,
        },
        {
          label: t('Pages viewed'),
          value: totals ? formatCount(totals.views, language) : '-',
          ...kpiChange(changeOf(totals?.views, prevTotals?.views)),
          caption,
        },
        {
          label: onPage ? t('Time on page') : t('Time on site'),
          value: timeText(time),
          ...kpiChange(changeOf(time, prevTime)),
          caption,
        },
      ],
      trend:
        trend.length >= 2
          ? {
              title: trendVisitorsUnavailable ? t('Pages viewed over time') : t('Visitors over time'),
              labels: trend.map((p) =>
                a.window.bucket !== 'hour'
                  ? dayFmt.format(p.at)
                  : twoDays
                    ? `${dayFmt.format(p.at)} ${hourFmt.format(p.at)}`
                    : hourFmt.format(p.at),
              ),
              series: [
                ...(!trendVisitorsUnavailable
                  ? [{ name: t('Visitors'), rgb: REPORT_SERIES_RGB.visitors, points: trend.map((p) => p.visitors) }]
                  : []),
                { name: t('Pages viewed'), rgb: REPORT_SERIES_RGB.views, points: trend.map((p) => p.views) },
              ],
            }
          : null,
      tables,
      footnote,
      pageOf: t('Page {{n}} of {{total}}', { n: '{{n}}', total: '{{total}}' }),
      fileName: `${(brandName || 'website').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-website-${new Date().toISOString().slice(0, 10)}`,
    };
    // Quality mode: the report on screen gives the period, addresses, notes, figures, trend and breakdown.
    if (!quality || !report) return doc;
    return {
      ...doc,
      ...qualityReportPdf({
        report,
        t,
        language,
        interval,
        held: qr.held,
        filters: filterLines,
        onPage: onPage !== null,
      }),
    };
  };

  // Quality mode: the report's data notes beside the download.
  const notes = report ? dataNotes(report, qr.held) : [];
  const ageMs = report ? dataAgeMs(report) : null;
  const updatedMinutes = ageMs !== null && ageMs <= FRESH_MS ? Math.max(0, Math.floor(ageMs / MINUTE_MS)) : null;
  const visitorsFigure = quality ? totals?.visitors ?? null : totals && !visitorUnavailable ? totals.visitors : null;
  const visitsFigure = quality ? totals?.visits ?? null : totals && !visitorUnavailable ? totals.visits : null;
  const exportButton = (
    <ExportPdfButton onExport={exportPdf} disabled={!canExport || exportQueued} busy={exportQueued} />
  );
  const summaryAction = quality ? (
    <SummaryActions>
      <DataNotes notes={notes} />
      {exportButton}
    </SummaryActions>
  ) : (
    exportButton
  );
  const summaryContext = summaryFilters.length > 0 ? <SummaryFilters items={summaryFilters} /> : undefined;

  // No address is counted (the brand has none public or internal, or its list cannot be read): the note stands in for
  // every figure, so nothing reads as 0.
  if (report && emptyScope) {
    const unreadable = report.hostCatalog.status === 'unavailable';
    return (
      <Page>
        {header}
        {sheet}
        {assetOverlay.node}
        <Toast toast={exportToast.toast} />
        {qr.status === 'error' && qr.error ? qualityFailure(qr.error) : null}
        <Summary
          lead={
            unreadable
              ? t('Your address list could not be read. Try again in a few minutes.')
              : t('No public address yet. Choose addresses to see their numbers.')
          }
          follow={[]}
          action={summaryAction}
          context={summaryContext}
        >
          <NoteAction>
            {unreadable ? (
              <Button label={t('Try again')} variant="outline" icon="refresh" onClick={qr.reload} />
            ) : (
              <Button label={t('Choose addresses')} variant="outline" icon="filter" onClick={chooseAddresses} />
            )}
          </NoteAction>
        </Summary>
      </Page>
    );
  }

  return (
    <Page>
      {header}
      {sheet}
      {assetOverlay.node}
      <ExportSnackbar open={exportQueued} message={t('Preparing your PDF…')} />
      <Toast toast={exportToast.toast} />

      {!quality && a.error ? (
        <Notice
          title={t('We could not load your website stats')}
          body={t(a.error)}
          actionLabel={t('Try again')}
          onAction={a.reload}
        />
      ) : null}
      {/* A failed read keeps the last figures in view, with what failed above them. */}
      {quality && qr.status === 'error' && qr.error ? qualityFailure(qr.error) : null}
      {trendVisitorsUnavailable || visitorUnavailable ? (
        quality ? (
          <Footnote text="People counts are unavailable for this selection by page or over time." tag="span" />
        ) : (
          <Footnote text="Visitor counts are unavailable for this selection by page or over time." tag="span" />
        )
      ) : null}
      <Summary
        lead={summary.lead}
        follow={summary.follow}
        loading={totalsLoading}
        action={summaryAction}
        context={summaryContext}
        countLine={report ? countLine(report.scope.filterBasis.people, report.scope.selectedHosts.length) : undefined}
      >
        <Tiles>
          <KpiTile
            label={t(w('Visitors'))}
            hint={
              onPage === 'pages'
                ? t(w('Different people who opened these pages'))
                : onPage
                  ? t(w('Different people who opened this page'))
                  : t(w('Different people who opened your website'))
            }
            figure={visitorsFigure === null ? null : { value: visitorsFigure }}
            change={changeOf(visitorsFigure, prevTotals?.visitors)}
            caption={caption}
            loading={totalsLoading}
            error={!quality && a.totals.status === 'error'}
          />
          <KpiTile
            label={t('Visits')}
            hint={
              onPage === 'pages'
                ? t('Times these pages were opened')
                : onPage
                  ? t('Times this page was opened')
                  : t('Times your website was opened')
            }
            figure={visitsFigure === null ? null : { value: visitsFigure }}
            change={changeOf(visitsFigure, prevTotals?.visits)}
            caption={caption}
            loading={totalsLoading}
            error={!quality && a.totals.status === 'error'}
          />
          <KpiTile
            label={t('Pages viewed')}
            hint={
              onPage === 'pages'
                ? t('How many times these pages were viewed')
                : onPage
                  ? t('How many times this page was viewed')
                  : t('All the pages people looked at')
            }
            figure={totals ? { value: totals.views } : null}
            change={changeOf(totals?.views, prevTotals?.views)}
            caption={caption}
            loading={totalsLoading}
            error={!quality && a.totals.status === 'error'}
          />
          <KpiTile
            label={onPage ? t('Time on page') : t('Time on site')}
            hint={
              onPage === 'pages'
                ? t('How long people stay on these pages')
                : onPage
                  ? t('How long people stay on this page')
                  : t('How long a visit lasts on average')
            }
            figure={timeFigure(time, t)}
            change={changeOf(time, prevTime)}
            caption={caption}
            loading={waiting || (!quality && a.time.status === 'loading')}
            error={!quality && a.time.status === 'error'}
          />
        </Tiles>
        {updatedMinutes !== null ? (
          <UpdatedLine text="Updated {{n}} minutes ago." placeholders={{ n: updatedMinutes }} tag="span" />
        ) : null}
        {onPage === 'landing' && picked ? (
          <PageConversions
            landingPageId={picked.landingPageId}
            resolving={landing.loading}
            resolveFailed={landing.error !== null || landing.partial}
            current={a.window.current}
            previous={a.window.previous}
            caption={caption}
          />
        ) : null}
      </Summary>

      {/* Traffic and pages (two thirds) beside what people use (a third); on a narrow screen the first group
          stays together above the second. */}
      <Split>
        <Column>
          <TrafficTrend
            visitorsAvailable={!trendVisitorsUnavailable}
            points={quality ? view?.trend ?? [] : a.trend.data ?? []}
            hourly={quality ? report?.scope.timeBucket === 'hour' : a.window.bucket === 'hour'}
            loading={waiting || (!quality && a.trend.status === 'loading')}
            error={!quality && a.trend.status === 'error'}
            quality={quality}
            suspected={quality ? view?.suspectedTrend ?? [] : undefined}
            excluded={quality ? view?.excludedTrend ?? [] : undefined}
          />
          {quality ? (
            <TrafficBreakdown
              breakdown={report?.current.breakdown ?? null}
              rulesVersion={report?.meta.rulesVersion ?? ''}
              loading={waiting}
            />
          ) : null}
          {!pagesListed ? null : (
            <StatCard title={t('Most visited pages')} sub={t('The pages people open most, and how long they stay')}>
              <RankedList
                items={pageItems}
                loading={waiting || (!quality && a.pages.status === 'loading')}
                error={!quality && a.pages.status === 'error'}
                emptyText={t('No pages visited in this period yet.')}
                errorText={t('We could not load your pages. Try again in a moment.')}
                placeholderRows={5}
              />
            </StatCard>
          )}
          {!onPage && showAddresses ? (
            <StatCard title={t('Website and landing pages')} sub={t(w('Visitors on each of your addresses'))}>
              <RankedList
                items={hostItems}
                loading={waiting || (!quality && a.sites.status === 'loading')}
                error={!quality && a.sites.status === 'error'}
                emptyText={hostsUnavailable ? unavailableLine : t('No visits in this period yet.')}
                errorText={t('We could not load this. Try again in a moment.')}
                placeholderRows={2}
              />
            </StatCard>
          ) : null}
        </Column>
        <Column>
          {sourcesCard}
          {devicesCard}
        </Column>
      </Split>

      <Footnote text={footnote} tag="span" raw $readable={quality} />
    </Page>
  );
};

export default WebsiteAnalyticsContent;
